<template>
  <div class="gof-page">
    <div class="gof-topbar">
      <div class="gof-crumb">
        <a class="gof-crumb-muted" href="/apps/smartlinks/new-global-offers" @click.prevent="$router.push({ name: 'NewGlobalOffers' })">Global Offers</a>
        <span class="gof-crumb-sep">/</span>
        <span class="gof-crumb-current">{{ pageModeLabel }}</span>
      </div>
    </div>

    <div v-if="loading || loadingEdit" class="gof-loading">{{ loadingEdit ? 'Loading offer…' : 'Loading…' }}</div>

    <div v-else class="gof-container">
      <p v-if="editLoadError" class="gof-err-text" style="margin-bottom:12px">
        <i class="fa fa-exclamation-triangle"></i> {{ editLoadError }}
      </p>
      <div v-if="(isEditMode || isMultiEditMode || isDuplicateMode) && !editLoadError" class="gof-edit-banner">
        <template v-if="isMultiEditMode">
          Editing {{ multiEditIds.length }} offers — locked fields are hidden; only filled fields are applied
        </template>
        <template v-else-if="isDuplicateMode">
          Duplicating from global offer #{{ duplicateFromId }} — review values, then create a new Affise offer
        </template>
        <template v-else>
          Editing global offer #{{ editOfferId }}
          <span v-if="editSessionOffers.length > 1"> · {{ editSessionOffers.length }} related rows</span>
        </template>
      </div>
      <p v-if="loadingRefs" class="gof-hint" style="margin-bottom:12px">Loading AF accounts &amp; agencies…</p>
      
      <section class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-building"></i></div>
          <div class="gof-section-heading">
            <h2 class="gof-section-title">Main</h2>
            <p class="gof-section-sub">Advertiser, app and targeting</p>
          </div>
          <div v-if="mainAffiseIds.length" class="gof-section-affise">
            Affise
            <span
              v-for="(id, i) in mainAffiseIds"
              :key="'aff-' + id"
              class="gof-section-affise-id"
            >#{{ id }}<template v-if="i < mainAffiseIds.length - 1"> · </template></span>
          </div>
        </header>
        <div class="gof-section-body">
          <div v-if="!isMultiEditMode" class="gof-field">
            <label class="gof-label">
              <span class="req">*</span> Advertiser
              <span class="gof-hint">
                {{ isEditMode && !advUnlockedForEdit
                  ? 'Locked in edit mode — unlock requires double confirmation'
                  : 'Type part of the company name, then pick' }}
              </span>
            </label>
            <div>
              <div
                class="gof-combo"
                ref="advBox"
                :class="{ 'is-open': showAdvDropdown, 'is-locked': isEditMode && !advUnlockedForEdit }"
              >
                <div class="gof-combo-input-wrap">
                  <i class="fa fa-search"></i>
                  <input
                    type="text"
                    class="gof-input"
                    :value="advQuery"
                    :disabled="isEditMode && !advUnlockedForEdit"
                    placeholder="Type an advertiser name…"
                    @focus="!(isEditMode && !advUnlockedForEdit) && (advOpen = true)"
                    @input="handleAdvInput"
                  />
                  <button
                    v-if="advQuery && !(isEditMode && !advUnlockedForEdit)"
                    type="button"
                    class="gof-combo-clear"
                    @click="clearAdvertiser"
                    aria-label="Clear"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                  <button
                    type="button"
                    class="gof-combo-chevron"
                    :disabled="!!advQuery.trim() || (isEditMode && !advUnlockedForEdit)"
                    :title="advQuery.trim() ? 'Clear the field to browse all' : 'Browse all advertisers'"
                    aria-label="Browse all advertisers"
                    @click="toggleAdvFullList"
                  >
                    <i class="fa" :class="showAdvDropdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
                </div>
                <ul v-if="showAdvDropdown && !(isEditMode && !advUnlockedForEdit)" class="gof-combo-dropdown">
                  <li v-if="advSearching && advMatches.length === 0" class="gof-combo-status">
                    <i class="fa fa-spinner fa-spin"></i> Searching…
                  </li>
                  <li v-for="a in advMatches" :key="'adv-' + a.id">
                    <button type="button" :class="{ active: String(advertiserId) === String(a.id) }" @click.prevent="pickAdvertiser(a)">
                      <span>
                        <span style="font-size:0.875rem;color:#334155">{{ a.name }}</span>
                        <span v-if="a.affiseAdvId" class="gof-mono" style="display:block;font-size:0.75rem;color:#94a3b8">
                          Affise {{ a.affiseAdvId }}
                        </span>
                      </span>
                    </button>
                  </li>
                  <li v-if="!advSearching && advTouched && advQuery.trim() && advMatches.length === 0" class="gof-combo-status">
                    No advertisers match "{{ advQuery.trim() }}"
                  </li>
                  <li v-if="!advSearching && !advQuery.trim() && advMatches.length === 0 && advBrowsingAll" class="gof-combo-status">
                    No advertisers found
                  </li>
                </ul>
              </div>
              <p v-if="selectedAdv" class="gof-hint" style="margin-top:4px">
                Affise advertiser ID: <span class="gof-mono">{{ selectedAdv.affiseAdvId || '—' }}</span>
              </p>
              <button
                v-if="isEditMode && !advUnlockedForEdit"
                type="button"
                class="gof-link-btn"
                style="margin-top:8px"
                @click="requestAdvUnlock"
              >
                <i class="fa fa-unlock-alt"></i> Change advertiser…
              </button>
              <p v-if="isEditMode && advUnlockedForEdit" class="gof-note" style="margin-top:8px">
                Advertiser unlock active — pick a new advertiser carefully.
              </p>
            </div>
          </div>

          <div v-if="!isMultiEditMode" class="gof-pair">
            <div>
              <label class="gof-half-label">
                <span class="req">*</span> Bundle ID
                <span class="gof-hint">Search by ID or app name, then pick</span>
              </label>
              <div class="gof-combo" ref="bundleBox" :class="{ 'is-open': showBundleDropdown, 'is-locked': isEditMode }" style="margin-top:4px">
                <div class="gof-combo-input-wrap">
                  <i class="fa fa-search"></i>
                  <input
                    type="text"
                    class="gof-input gof-input-mono"
                    :value="bundleQuery"
                    :disabled="isEditMode"
                    placeholder="Type a Bundle ID or app name…"
                    @focus="!isEditMode && (bundleOpen = true)"
                    @input="handleBundleInput"
                  />
                  <button v-if="bundleQuery && !isEditMode" type="button" class="gof-combo-clear" @click="clearBundle" aria-label="Clear">
                    <i class="fa fa-times"></i>
                  </button>
                  <button
                    type="button"
                    class="gof-combo-chevron"
                    :disabled="isEditMode || !!bundleQuery.trim() || !!bundle"
                    :title="bundleQuery.trim() || bundle ? 'Clear the field to browse all' : 'Browse all bundles'"
                    aria-label="Browse all bundles"
                    @click="toggleBundleFullList"
                  >
                    <i class="fa" :class="showBundleDropdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
                </div>
                <ul v-if="showBundleDropdown && !isEditMode" class="gof-combo-dropdown">
                  <li v-if="bundleSearching && bundleMatches.length === 0" class="gof-combo-status">
                    <i class="fa fa-spinner fa-spin"></i> Searching…
                  </li>
                  <li v-for="b in bundleMatches" :key="'b-' + b.bundle_id">
                    <button type="button" :class="{ active: bundleId === b.bundle_id }" @click.prevent="pickBundle(b)">
                      <span>
                        <span style="font-size:0.875rem;color:#334155">{{ b.app_name }}</span>
                        <span class="gof-mono" style="display:block;font-size:0.75rem;color:#94a3b8">{{ b.bundle_id }}</span>
                      </span>
                      <span class="gof-combo-meta">{{ b.os }} · {{ b.vertical_name }}</span>
                    </button>
                  </li>
                  <li v-if="!bundleSearching && bundleTouched && bundleQuery.trim() && bundleMatches.length === 0" class="gof-combo-status">
                    No bundle matches "{{ bundleQuery.trim() }}"
                  </li>
                  <li v-if="!bundleSearching && !bundleQuery.trim() && bundleMatches.length === 0 && bundleBrowsingAll" class="gof-combo-status">
                    No bundles found
                  </li>
                </ul>
                <div v-if="bundle" class="gof-ok-chip">
                  <i class="fa fa-check"></i>
                  <span style="font-weight:500">{{ bundle.app }}</span>
                  <span class="gof-mono" style="font-size:0.75rem">{{ bundleId }}</span>
                  <span style="font-size:0.75rem">· {{ bundle.os }} · {{ bundle.vertical }}</span>
                </div>
                <div v-if="bundleTouched && !bundleSearching && bundleNoMatch && !bundle" class="gof-err-row">
                  <span class="gof-err-text">
                    <i class="fa fa-exclamation-triangle"></i>
                    No bundle matches "{{ bundleQuery.trim() }}".
                  </span>
                  <a :href="ADD_BUNDLE_URL" target="_blank" class="gof-btn-primary" style="padding:6px 12px;font-size:0.875rem;text-decoration:none">
                    <i class="fa fa-plus"></i> Add new Bundle
                  </a>
                </div>
              </div>
            </div>
            <div>
              <label class="gof-half-label">
                App Name
                <span class="gof-hint">Auto-loaded from the bundle list</span>
              </label>
              <input class="gof-input" style="margin-top:4px" :value="bundle ? bundle.app : ''" disabled placeholder="—" />
            </div>
          </div>

          <div class="gof-pair">
            <div>
              <label class="gof-half-label">
                <span class="req">*</span> Country
                <span class="gof-hint">{{ isMultiEditMode ? 'Leave empty to keep existing' : 'Type to filter, then pick' }}</span>
              </label>
              <div class="gof-combo" ref="countryBox" :class="{ 'is-open': showCountryDropdown }" style="margin-top:4px">
                <div class="gof-combo-input-wrap">
                  <i class="fa fa-search"></i>
                  <input
                    type="text"
                    class="gof-input"
                    :value="countryQuery"
                    placeholder="Type a country code or name…"
                    @focus="countryOpen = true"
                    @input="handleCountryInput"
                  />
                  <button v-if="countryQuery" type="button" class="gof-combo-clear" @click="clearCountry" aria-label="Clear">
                    <i class="fa fa-times"></i>
                  </button>
                  <button
                    type="button"
                    class="gof-combo-chevron"
                    :disabled="!!countryQuery.trim()"
                    :title="countryQuery.trim() ? 'Clear the field to browse all' : 'Browse all countries'"
                    aria-label="Browse all countries"
                    @click="toggleCountryFullList"
                  >
                    <i class="fa" :class="showCountryDropdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
                </div>
                <ul v-if="showCountryDropdown" class="gof-combo-dropdown">
                  <li v-for="c in countryMatches" :key="'c-' + c.value">
                    <button type="button" :class="{ active: country === c.value }" @click.prevent="pickCountry(c)">
                      <span style="font-size:0.875rem;color:#334155">{{ c.label }}</span>
                    </button>
                  </li>
                  <li v-if="countryTouched && countryQuery.trim() && countryMatches.length === 0" class="gof-combo-status">
                    No countries match "{{ countryQuery.trim() }}"
                  </li>
                  <li v-if="!countryQuery.trim() && countryMatches.length === 0 && countryBrowsingAll" class="gof-combo-status">
                    No countries found
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="!isMultiEditMode">
              <label class="gof-half-label">
                OS
                <span class="gof-hint">Auto-selected from the bundle</span>
              </label>
              <input class="gof-input" style="margin-top:4px" :value="bundle ? bundle.os : ''" disabled placeholder="—" />
            </div>
          </div>

          <div v-if="!isMultiEditMode" class="gof-field">
            <label class="gof-label">
              Vertical
              <span class="gof-hint">Auto-selected from the bundle</span>
            </label>
            <input class="gof-input" :value="bundle ? bundle.vertical : ''" disabled placeholder="—" />
          </div>

          <div v-if="!isMultiEditMode" class="gof-field">
            <label class="gof-label">
              <span class="req">*</span> Attribution
              <span class="gof-hint">{{ isEditMode ? 'Locked in edit mode' : 'Click-through, view-through, or One link' }}</span>
            </label>
            <div class="gof-pills">
              <button type="button" class="gof-pill" :class="{ active: attribution === 'CTA' }" :disabled="isEditMode" @click="attribution = 'CTA'">CTA</button>
              <button type="button" class="gof-pill" :class="{ active: attribution === 'VTA' }" :disabled="isEditMode" @click="attribution = 'VTA'">VTA</button>
              <button type="button" class="gof-pill" :class="{ active: attribution === 'One link' }" :disabled="isEditMode" @click="attribution = 'One link'">One link</button>
            </div>
          </div>

          <div class="gof-field">
            <label class="gof-label">
              <span v-if="!isMultiEditMode" class="req">*</span> Offer Type
              <span class="gof-hint">{{ isMultiEditMode ? 'Leave empty to keep existing — only applied if selected' : 'CPA is default' }}</span>
            </label>
            <div class="gof-pills">
              <button type="button" class="gof-pill" :class="{ active: offerType === 'CPA' }" @click="offerType = 'CPA'">CPA</button>
              <button type="button" class="gof-pill" :class="{ active: offerType === 'CPI' }" @click="offerType = 'CPI'">CPI</button>
              <button
                v-if="isMultiEditMode && offerType"
                type="button"
                class="gof-link-btn"
                style="margin:0 0 0 8px"
                @click="offerType = ''"
              >
                Clear
              </button>
            </div>
          </div>

          <div v-if="bundle && !isMultiEditMode" class="gof-field">
            <label class="gof-label">
              <span class="req">*</span> MMP
              <span class="gof-hint">{{ isEditMode ? 'Locked in edit mode' : 'Set from the bundle — change if needed' }}</span>
            </label>
            <div class="gof-select-wrap">
              <select class="gof-select" v-model="mmp" :disabled="isEditMode">
                <option v-for="m in MMPS" :key="m.value + m.label" :value="m.value">{{ m.label }}</option>
              </select>
              <i class="fa fa-chevron-down"></i>
            </div>
          </div>

          <template v-if="bundle && isAppsFlyer && !isMultiEditMode">
            <div class="gof-field">
              <label class="gof-label">
                <span class="req">*</span> AppsFlyer Account
                <span class="gof-hint">
                  {{ isEditMode
                    ? 'Locked in edit mode — AF seat cannot be changed'
                    : 'Multi-select — one offer is created per account. O&O accounts listed first; others under See more.' }}
                </span>
              </label>
              <div>
                <div class="gof-check-list">
                  <label v-for="acc in afOnoAccounts" :key="'ono-' + acc.value" class="gof-check" :class="{ 'is-locked': isEditMode }">
                    <input type="checkbox" :checked="afAccounts.includes(acc.label)" :disabled="isEditMode" @change="toggleAccount(acc.label)" />
                    {{ acc.label }}
                  </label>
                </div>

                <template v-if="afShowOtherAccounts">
                  <div class="gof-subsection-label">Accounts without O&amp;O — not in main list</div>
                  <div class="gof-check-list">
                    <label v-for="acc in afOtherAccounts" :key="'other-' + acc.value" class="gof-check" :class="{ 'is-locked': isEditMode }">
                      <input type="checkbox" :checked="afAccounts.includes(acc.label)" :disabled="isEditMode" @change="toggleAccount(acc.label)" />
                      {{ acc.label }}
                    </label>
                  </div>
                </template>

                <button
                  v-if="afOtherAccounts.length && !isEditMode"
                  type="button"
                  class="gof-link-btn"
                  style="margin-top:8px"
                  @click="afShowOtherAccounts = !afShowOtherAccounts"
                >
                  {{ afShowOtherAccounts ? 'Show fewer' : `See more (${afOtherAccounts.length} other accounts)` }}
                </button>
              </div>
            </div>
            <div class="gof-field">
              <label class="gof-label">
                AppsFlyer Agency
                <span class="gof-hint">Single select only</span>
              </label>
            <div class="gof-select-extra">
              <div class="gof-select-wrap">
                <select class="gof-select" v-model="afAgency">
                  <option value="none">none</option>
                  <option v-for="a in afAgenciesForSelect" :key="a.value + a.label" :value="a.label">{{ a.label }}</option>
                  <option value="Other">Other</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <input
                v-if="isOtherAgency"
                class="gof-input"
                :value="afAgencyOther"
                placeholder="Agency name"
                @input="onAgencyOtherInput($event.target.value)"
              />
            </div>
            </div>
          </template>

          <div v-if="isMultiEditMode" class="gof-field">
            <label class="gof-label">
              AppsFlyer Agency
              <span class="gof-hint">Leave as none to keep existing — applied only if changed</span>
            </label>
            <div class="gof-select-extra">
              <div class="gof-select-wrap">
                <select class="gof-select" v-model="afAgency">
                  <option value="none">none (no change)</option>
                  <option v-for="a in afAgenciesForSelect" :key="a.value + a.label" :value="a.label">{{ a.label }}</option>
                  <option value="Other">Other</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <input
                v-if="isOtherAgency"
                class="gof-input"
                :value="afAgencyOther"
                placeholder="Agency name"
                @input="onAgencyOtherInput($event.target.value)"
              />
            </div>
          </div>
        </div>
      </section>

      
      <section class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-tag"></i></div>
          <div>
            <h2 class="gof-section-title">Offer Name</h2>
            <p class="gof-section-sub">
              {{ isMultiEditMode ? 'Existing titles are kept for each selected offer' : 'Auto-assembled — pushed to Affise as the offer title' }}
            </p>
          </div>
        </header>
        <div class="gof-section-body">
          <div v-if="!isEditMode && !isMultiEditMode" class="gof-field">
            <label class="gof-label">
              Free text
              <span class="gof-hint">Optional — inserted after AF account / PID, before offer type · GEO · OS · CTA/VTA</span>
            </label>
            <input class="gof-input" v-model="offerFreeText" placeholder="e.g. Q3 push" />
          </div>
          <p v-else-if="isEditMode && !isMultiEditMode" class="gof-hint" style="padding:12px 0 0">
            Offer names update live from the fields below and sync to Affise on save. A trailing "Duplicate" is dropped from the name.
          </p>
          <div style="padding:12px 0">
            <p class="gof-hint" style="margin-bottom:8px">
              {{ isMultiEditMode ? `Selected offers (${offerNames.length})` : `Preview${offerNames.length > 1 ? ` (${offerNames.length} offers)` : ''}` }}
            </p>
            <div v-for="(o, i) in offerNames" :key="i" class="gof-preview" style="margin-bottom:6px">
              <span v-if="o.name">{{ o.name }}</span>
              <span v-else class="gof-preview-empty">— fill in the fields above —</span>
            </div>
          </div>
        </div>
      </section>

      
      <section class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-link"></i></div>
          <div>
            <h2 class="gof-section-title">Tracking</h2>
            <p class="gof-section-sub">Click and impression URLs</p>
          </div>
        </header>
        <div class="gof-section-body">
          <div class="gof-field">
            <label class="gof-label">
              Advertiser default URL
              <span class="gof-hint">{{ isMultiEditMode ? 'Leave empty to keep existing' : 'Prefilled from the advertiser — editable' }}</span>
            </label>
            <input class="gof-input gof-input-mono" v-model="advDefaultUrl" placeholder="https://…" />
          </div>
          <div v-if="isVTA && !isMultiEditMode" class="gof-field">
            <label class="gof-label">
              Advertiser default IMP URL
              <span class="gof-hint">VTA only — prefilled from the advertiser, editable</span>
            </label>
            <input class="gof-input gof-input-mono" v-model="advImpDefaultUrl" placeholder="https://…" />
          </div>
          <div v-if="isOneLink && !isMultiEditMode" class="gof-field">
            <label class="gof-label">
              <span class="req">*</span> One Link URL
              <span class="gof-hint">Used as the domain and path of the generated tracking link</span>
            </label>
            <input
              class="gof-input gof-input-mono"
              :value="manualUrl"
              placeholder="go.example.com/path"
              @input="onManualUrlInput($event.target.value)"
            />
          </div>
          <div class="gof-field">
            <label class="gof-label">
              Tracking links
              <span v-if="isMultiEditMode" class="gof-hint">Leave empty to keep existing URLs — applied to all selected offers if filled</span>
              <span v-else-if="usesLinkGenerator" class="gof-hint">Create click and impression URLs with the link generator</span>
              <span v-else class="gof-hint">Paste the tracking URL{{ isVTA ? 's' : '' }} for this MMP</span>
            </label>
            <div v-if="!isMultiEditMode && isAppsFlyer">
              <button type="button" class="gof-link-btn" @click="openAfGenerator">
                <i class="fa fa-magic"></i> Generate with AppsFlyer Link Generator
              </button>
            </div>
            <div v-else-if="!isMultiEditMode && mmp === 'Adjust'">
              <button type="button" class="gof-link-btn" @click="openAdjustGenerator">
                <i class="fa fa-magic"></i> Open Adjust Link Generator
              </button>
            </div>
            <div v-else class="gof-manual-tracking">
              <div class="gof-field" style="margin:0;padding:0;border:0">
                <label class="gof-half-label">Click URL <span v-if="!isMultiEditMode" class="req">*</span></label>
                <input
                  class="gof-input gof-input-mono"
                  :value="trackingUrl"
                  placeholder="https://…"
                  @input="onPastedTrackingUrl('trackingUrl', $event.target.value)"
                />
              </div>
              <div v-if="isVTA && !isMultiEditMode" class="gof-field" style="margin:0;padding:0;border:0;margin-top:10px">
                <label class="gof-half-label">Impression URL <span class="req">*</span></label>
                <input
                  class="gof-input gof-input-mono"
                  :value="impressionUrl"
                  placeholder="https://…"
                  @input="onPastedTrackingUrl('impressionUrl', $event.target.value)"
                />
              </div>
            </div>
          </div>
          <div v-if="generatedLinks.length && usesLinkGenerator && !isMultiEditMode" style="padding:12px 0">
            <p class="gof-hint" style="margin-bottom:8px">
              <i class="fa fa-th-large"></i>
              {{ generatedLinkUrlCount }} link{{ generatedLinkUrlCount === 1 ? '' : 's' }} generated
              <template v-if="generatedLinks.length > 1">
                across {{ generatedLinks.length }} accounts
              </template>.
            </p>
            <div v-for="(l, i) in generatedLinks" :key="i" class="gof-link-group">
              <div v-if="l.account" class="gof-link-account">{{ l.account }}</div>
              <div class="gof-preview gof-link-row gof-link-row--click">
                <div class="gof-link-toolbar">
                  <div class="gof-link-kind">Click</div>
                  <button type="button" class="gof-link-btn" @click="toggleGeneratedLinkEdit(i, 'click')">
                    {{ isGeneratedLinkEditing(i, 'click') ? 'Done' : 'Edit' }}
                  </button>
                </div>
                <textarea
                  v-if="isGeneratedLinkEditing(i, 'click')"
                  class="gof-input gof-input-mono gof-link-url-input"
                  :value="l.click"
                  spellcheck="false"
                  placeholder="https://…"
                  @input="onGeneratedUrlInput(l, 'click', $event.target.value)"
                ></textarea>
                <div v-else class="gof-link-url">{{ l.click }}</div>
              </div>
              <div v-if="isVTA" class="gof-preview gof-link-row gof-link-row--imp">
                <div class="gof-link-toolbar">
                  <div class="gof-link-kind">Imp</div>
                  <button type="button" class="gof-link-btn" @click="toggleGeneratedLinkEdit(i, 'impression')">
                    {{ isGeneratedLinkEditing(i, 'impression') ? 'Done' : 'Edit' }}
                  </button>
                </div>
                <textarea
                  v-if="isGeneratedLinkEditing(i, 'impression')"
                  class="gof-input gof-input-mono gof-link-url-input"
                  :value="l.impression"
                  spellcheck="false"
                  placeholder="https://…"
                  @input="onGeneratedUrlInput(l, 'impression', $event.target.value)"
                ></textarea>
                <div v-else class="gof-link-url">{{ l.impression }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      
      <section v-if="false" class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-bullseye"></i></div>
          <div>
            <h2 class="gof-section-title">Advertiser KPI</h2>
            <p class="gof-section-sub">Editing here updates all offers with the same advertiser + bundle ID</p>
          </div>
        </header>
        <div class="gof-section-body">
          <div v-for="row in kpiRows" :key="row.id" style="padding:8px 0;border-bottom:1px solid #f1f5f9">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="font-size:0.875rem;color:#475569;width:160px;flex-shrink:0">{{ kpiLabel(row.key) }}</span>
              <template v-if="!canSplitKpi(row) || row.mode === 'shared'">
                <input class="gof-input" style="flex:1;min-width:160px;max-width:none" v-model="row.value" placeholder="e.g. ≥ 15%, ≤ 4h, 1:3" />
              </template>
              <template v-else>
                <div style="display:flex;align-items:center;gap:8px;flex:1;min-width:280px">
                  <span class="gof-hint" style="width:32px">CTA</span>
                  <input class="gof-input" style="flex:1;max-width:none" v-model="row.valueCTA" placeholder="value" />
                  <span class="gof-hint" style="width:32px">VTA</span>
                  <input class="gof-input" style="flex:1;max-width:none" v-model="row.valueVTA" placeholder="value" />
                </div>
              </template>
              <button
                v-if="canSplitKpi(row)"
                type="button"
                class="gof-btn-sm"
                :class="{ active: row.mode === 'split' }"
                @click="row.mode = row.mode === 'split' ? 'shared' : 'split'"
              >
                {{ row.mode === 'split' ? 'Split CTA/VTA' : 'Shared' }}
              </button>
              <button type="button" class="gof-icon-btn" @click="removeKpi(row.id)" aria-label="Remove KPI">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
          <div style="padding:12px 0;display:flex;align-items:center;gap:8px">
            <div class="gof-select-wrap" style="max-width:20rem">
              <select class="gof-select" v-model="kpiPick" @change="onAddKpi">
                <option value="">+ Add KPI…</option>
                <optgroup v-for="g in kpiGroups" :key="g || '_ungrouped'" :label="g || 'Other'">
                  <option v-for="d in availableKpis(g)" :key="d.key" :value="d.key">{{ d.label }}</option>
                </optgroup>
              </select>
              <i class="fa fa-chevron-down"></i>
            </div>
            <span v-if="!kpiRows.length" class="gof-hint">No KPIs added yet — only add what's relevant for this offer.</span>
          </div>
          <div class="gof-info-row">
            <i class="fa fa-info-circle"></i>
            <span>
              On edit, these values propagate to every related offer (matched by advertiser and bundle ID).
              <template v-if="isVTA"> KPIs marked splittable can carry separate CTA and VTA values; MTTI is always shared.</template>
            </span>
          </div>
        </div>
      </section>

      
      <section class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-sliders"></i></div>
          <div>
            <h2 class="gof-section-title">Caps</h2>
            <p class="gof-section-sub">Editing here updates all offers with the same advertiser + bundle ID</p>
          </div>
        </header>
        <div class="gof-section-body">
          <div style="padding:12px 0;border-bottom:1px solid #f1f5f9">
            <label class="gof-half-label" style="display:block;margin-bottom:8px">Click cap</label>
            <div v-for="cap in clickCaps" :key="cap.id" class="gof-cap-row">
              <input class="gof-input gof-cap-val" :value="cap.value" @input="cap.value = $event.target.value.replace(/[^0-9]/g, '')" placeholder="0" />
              <div class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="cap.timeframe" style="max-width:none;width:auto;text-transform:capitalize">
                  <option v-for="t in TIMEFRAMES" :key="t" :value="t">{{ t }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <div v-if="isVTA" class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="cap.scope" style="max-width:none;width:auto">
                  <option v-for="s in CAP_SCOPES" :key="s" :value="s">{{ s }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <button type="button" class="gof-icon-btn" @click="removeClickCap(cap.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <button type="button" class="gof-link-btn" @click="addClickCap"><i class="fa fa-plus"></i> Add click cap</button>
          </div>

          
          <div v-if="false" style="padding:12px 0;border-bottom:1px solid #f1f5f9">
            <label class="gof-half-label" style="display:block;margin-bottom:8px">Conversion cap</label>
            <div v-for="cap in conversionCaps" :key="cap.id" class="gof-cap-row">
              <input class="gof-input gof-cap-val" :value="cap.value" @input="cap.value = $event.target.value.replace(/[^0-9]/g, '')" placeholder="0" />
              <input class="gof-input gof-input-mono" style="flex:1;min-width:160px;max-width:none" v-model="cap.event" placeholder="event name (goal value)" />
              <div class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="cap.timeframe" style="max-width:none;width:auto;text-transform:capitalize">
                  <option v-for="t in TIMEFRAMES" :key="t" :value="t">{{ t }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <div v-if="isVTA" class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="cap.scope" style="max-width:none;width:auto">
                  <option v-for="s in CAP_SCOPES" :key="s" :value="s">{{ s }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <button v-if="conversionCaps.length > 1" type="button" class="gof-icon-btn" @click="removeConversionCap(cap.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
            <button type="button" class="gof-link-btn" @click="addConversionCap"><i class="fa fa-plus"></i> Add conversion cap</button>
          </div>

          <div v-if="false" style="padding:12px 0">
            <label class="gof-half-label" style="display:block;margin-bottom:8px">Budget cap</label>
            <div class="gof-cap-row">
              <input class="gof-input gof-cap-val" :value="budgetCap.value" @input="budgetCap.value = $event.target.value.replace(/[^0-9]/g, '')" placeholder="0" />
              <div class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="budgetCap.timeframe" style="max-width:none;width:auto;text-transform:capitalize">
                  <option v-for="t in TIMEFRAMES" :key="t" :value="t">{{ t }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
              <div v-if="isVTA" class="gof-select-wrap" style="max-width:none;width:auto">
                <select class="gof-select" v-model="budgetCap.scope" style="max-width:none;width:auto">
                  <option v-for="s in CAP_SCOPES" :key="s" :value="s">{{ s }}</option>
                </select>
                <i class="fa fa-chevron-down"></i>
              </div>
            </div>
          </div>

          <div class="gof-info-row">
            <i class="fa fa-info-circle"></i>
            <span>
              On edit, these values propagate to every related offer (matched by advertiser and bundle ID).
              <template v-if="isVTA"> Scope decides whether a cap applies to the CTA offer, the VTA offer, or both.</template>
            </span>
          </div>
        </div>
      </section>

      
      <section class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-usd"></i></div>
          <div>
            <h2 class="gof-section-title">Payout</h2>
            <p class="gof-section-sub">Each row = one Affise common payout, pushed via API</p>
          </div>
        </header>
        <div class="gof-section-body">
          <div style="padding:12px 0">
            <div class="gof-table-wrap">
              <table class="gof-table">
                <thead>
                  <tr>
                    <th style="text-align:center;width:32px">#</th>
                    <th style="width:12rem">Goal value</th>
                    <th>Payment type</th>
                    <th style="width:6rem">Revenue</th>
                    <th style="width:12rem">Custom field2</th>
                    <th style="width:12rem">Custom field3</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody v-for="(row, i) in payoutRows" :key="row.id">
                    <tr>
                      <td style="text-align:center;font-size:0.75rem;color:#94a3b8">{{ i + 1 }}</td>
                      <td>
                        <input class="gof-input gof-input-mono" style="max-width:none" v-model="row.goalValue" placeholder="af_initiated_checkout" />
                      </td>
                      <td>
                        <div class="gof-select-wrap" style="max-width:none">
                          <select class="gof-select" style="max-width:none;width:auto" v-model="row.paymentType">
                            <option v-for="t in PAYMENT_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
                          </select>
                          <i class="fa fa-chevron-down"></i>
                        </div>
                      </td>
                      <td>
                        <div style="display:flex;align-items:center;gap:4px">
                          <span class="gof-hint">{{ row.paymentType === 'CPS(percent)' ? '%' : '$' }}</span>
                          <input
                            class="gof-input"
                            style="max-width:none"
                            :value="row.revenue"
                            @input="row.revenue = $event.target.value.replace(/[^0-9.]/g, '')"
                            placeholder="000.00"
                            maxlength="6"
                          />
                        </div>
                      </td>
                      <td>
                        <input
                          class="gof-input gof-input-mono"
                          style="max-width:none"
                          :value="row.custom['2']"
                          @input="row.custom['2'] = sanitizeCsv($event.target.value)"
                          placeholder="value1,value2"
                        />
                      </td>
                      <td>
                        <input
                          class="gof-input gof-input-mono"
                          style="max-width:none"
                          :value="row.custom['3']"
                          @input="row.custom['3'] = sanitizeCsv($event.target.value)"
                          placeholder="value1,value2"
                        />
                      </td>
                      <td style="white-space:nowrap">
                        <div style="display:flex;align-items:center;gap:4px;justify-content:flex-end">
                          <button
                            type="button"
                            class="gof-btn-sm"
                            :class="{ active: !!expandedIds[row.id] }"
                            @click="toggleExpand(row.id)"
                          >
                            <i class="fa" :class="expandedIds[row.id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            Advanced
                          </button>
                          <button type="button" class="gof-icon-btn blue" @click="duplicatePayoutRow(row.id)" title="Duplicate">
                            <i class="fa fa-copy"></i>
                          </button>
                          <button type="button" class="gof-icon-btn" @click="deletePayoutRow(row.id)" title="Delete">
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="expandedIds[row.id]" class="gof-adv-panel">
                      <td colspan="7">
                        <div class="gof-adv-grid">
                          <div>
                            <label class="gof-hint">Country</label>
                            <div
                              class="gof-combo gof-payout-country-combo"
                              :class="{ 'is-open': !!row._countryOpen }"
                              style="margin-top:4px"
                            >
                              <div class="gof-combo-input-wrap">
                                <i class="fa fa-search"></i>
                                <input
                                  type="text"
                                  class="gof-input"
                                  style="max-width:none"
                                  :value="row._countryQuery || ''"
                                  placeholder="All — type to pick a country"
                                  @focus="openPayoutCountry(row)"
                                  @input="handlePayoutCountryInput(row, $event)"
                                />
                                <button
                                  v-if="row._countryQuery"
                                  type="button"
                                  class="gof-combo-clear"
                                  @click="clearPayoutCountry(row)"
                                  aria-label="Clear"
                                >
                                  <i class="fa fa-times"></i>
                                </button>
                                <button
                                  type="button"
                                  class="gof-combo-chevron"
                                  :disabled="!!String(row._countryQuery || '').trim()"
                                  :title="String(row._countryQuery || '').trim() ? 'Clear the field to browse all' : 'Browse all countries'"
                                  aria-label="Browse all countries"
                                  @click="togglePayoutCountryFullList(row)"
                                >
                                  <i class="fa" :class="row._countryOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                </button>
                              </div>
                              <ul v-if="row._countryOpen && ((row._countryMatches && row._countryMatches.length) || String(row._countryQuery || '').trim() || row._countryBrowsingAll)" class="gof-combo-dropdown">
                                <li v-for="c in row._countryMatches || []" :key="'pc-' + row.id + '-' + c.value">
                                  <button type="button" :class="{ active: row.country === c.value }" @click.prevent="pickPayoutCountry(row, c)">
                                    <span style="font-size:0.875rem;color:#334155">{{ c.label }}</span>
                                  </button>
                                </li>
                                <li v-if="row._countryTouched && String(row._countryQuery || '').trim() && !(row._countryMatches && row._countryMatches.length)" class="gof-combo-status">
                                  No countries match "{{ String(row._countryQuery || '').trim() }}"
                                </li>
                                <li v-if="!String(row._countryQuery || '').trim() && !(row._countryMatches && row._countryMatches.length) && row._countryBrowsingAll" class="gof-combo-status">
                                  No countries found
                                </li>
                              </ul>
                            </div>
                            <p class="gof-hint">Leave empty for All. Affise needs an ISO code (e.g. US).</p>
                          </div>
                          <div>
                            <label class="gof-hint">OS</label>
                            <div class="gof-select-wrap" style="max-width:none;margin-top:4px">
                              <select class="gof-select" style="max-width:none" v-model="row.os">
                                <option v-for="o in OS_OPTIONS" :key="o" :value="o">{{ o }}</option>
                              </select>
                              <i class="fa fa-chevron-down"></i>
                            </div>
                          </div>
                          <div>
                            <label class="gof-hint">Currency</label>
                            <div class="gof-select-wrap" style="max-width:none;margin-top:4px">
                              <select class="gof-select" style="max-width:none" v-model="row.currency">
                                <option v-for="c in CURRENCIES" :key="c" :value="c">{{ c }}</option>
                              </select>
                              <i class="fa fa-chevron-down"></i>
                            </div>
                          </div>
                          <div>
                            <label class="gof-hint">Cities</label>
                            <div
                              class="gof-combo gof-payout-city-combo"
                              :class="{ 'is-open': !!row._cityOpen }"
                              style="margin-top:4px"
                            >
                              <div class="gof-combo-input-wrap">
                                <i class="fa fa-search"></i>
                                <input
                                  type="text"
                                  class="gof-input"
                                  style="max-width:none"
                                  :value="row._cityQuery || ''"
                                  :disabled="!row.country"
                                  :placeholder="row.country ? 'Type a city name…' : 'Pick a country first'"
                                  @focus="openPayoutCity(row)"
                                  @input="handlePayoutCityInput(row, $event)"
                                />
                                <button
                                  v-if="row._cityQuery"
                                  type="button"
                                  class="gof-combo-clear"
                                  @click="clearPayoutCityQuery(row)"
                                  aria-label="Clear"
                                >
                                  <i class="fa fa-times"></i>
                                </button>
                              </div>
                              <ul v-if="row._cityOpen && row.country" class="gof-combo-dropdown">
                                <li v-if="row._citySearching" class="gof-combo-status">
                                  <i class="fa fa-spinner fa-spin"></i> Searching…
                                </li>
                                <li v-for="c in row._cityMatches || []" :key="'city-' + row.id + '-' + c.id">
                                  <button type="button" @click.prevent="pickPayoutCity(row, c)">
                                    <span style="font-size:0.875rem;color:#334155">{{ c.name }}</span>
                                    <span class="gof-combo-meta">{{ c.region || c.region_code || '' }}</span>
                                  </button>
                                </li>
                                <li v-if="!row._citySearching && String(row._cityQuery || '').trim() && !(row._cityMatches && row._cityMatches.length)" class="gof-combo-status">
                                  No cities match "{{ String(row._cityQuery || '').trim() }}"
                                </li>
                              </ul>
                            </div>
                            <div v-if="row.cities && row.cities.length" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px">
                              <button
                                v-for="c in row.cities"
                                :key="'sel-city-' + row.id + '-' + c.id"
                                type="button"
                                class="gof-btn-sm"
                                @click="removePayoutCity(row, c.id)"
                              >
                                {{ c.name }} <i class="fa fa-times"></i>
                              </button>
                            </div>
                            <p class="gof-hint">Leave empty for All.</p>
                          </div>
                          <div>
                            <label class="gof-hint">
                              Payouts
                              <span v-if="row.paymentType === 'CPS(percent)'">(percent)</span>
                            </label>
                            <div style="display:flex;align-items:center;gap:4px;margin-top:4px">
                              <span class="gof-hint">{{ row.paymentType === 'CPS(percent)' ? '%' : '$' }}</span>
                              <input
                                class="gof-input"
                                style="max-width:none"
                                :value="row.payoutsEdited ? row.payouts : row.revenue"
                                @input="onPayoutsEdit(row, $event.target.value)"
                                placeholder="000.00"
                                maxlength="6"
                              />
                            </div>
                            <p class="gof-hint">
                              <button v-if="row.payoutsEdited" type="button" class="gof-link-btn" style="margin:0" @click="row.payouts = ''; row.payoutsEdited = false">
                                reset to Revenue
                              </button>
                              <span v-else>Defaults to Revenue.</span>
                            </p>
                          </div>
                          <div>
                            <label class="gof-hint">Goal title</label>
                            <input class="gof-input" style="max-width:none;margin-top:4px" v-model="row.goalTitle" placeholder="Default" />
                          </div>
                        </div>
                        <div style="margin-top:16px">
                          <label class="gof-hint">Devices</label>
                          <div class="gof-pills" style="margin-top:4px;flex-wrap:wrap">
                            <button
                              v-for="d in AFFISE_DEVICE_TYPES"
                              :key="'dev-' + row.id + '-' + d"
                              type="button"
                              class="gof-pill"
                              :class="{ active: (row.devices || []).includes(d) }"
                              @click="togglePayoutDevice(row, d)"
                            >
                              {{ d }}
                            </button>
                          </div>
                          <p class="gof-hint">Leave empty for All.</p>
                        </div>
                        <div style="margin-top:16px">
                          <p class="gof-hint" style="font-weight:500;margin-bottom:8px">Additional custom fields</p>
                          <div class="gof-adv-grid" style="margin-bottom:12px">
                            <div v-for="n in advCustomKeys(row)" :key="n">
                              <label class="gof-hint">
                                Custom field{{ n }}
                                <button type="button" class="gof-icon-btn" @click="removeCustom(row, n)"><i class="fa fa-trash"></i></button>
                              </label>
                              <input
                                class="gof-input gof-input-mono"
                                style="max-width:none;margin-top:4px"
                                :value="row.custom[n]"
                                @input="row.custom[n] = sanitizeCsv($event.target.value)"
                                placeholder="value1,value2"
                              />
                            </div>
                          </div>
                          <div v-if="availableCustom(row).length" style="display:flex;align-items:center;gap:8px">
                            <div class="gof-select-wrap" style="max-width:12rem">
                              <select class="gof-select" v-model="row._addN">
                                <option value="">Custom field…</option>
                                <option v-for="n in availableCustom(row)" :key="n" :value="n">Custom field{{ n }}</option>
                              </select>
                              <i class="fa fa-chevron-down"></i>
                            </div>
                            <button type="button" class="gof-link-btn" style="margin:0" :disabled="!row._addN" @click="addCustom(row)">
                              <i class="fa fa-plus"></i> Add
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="gof-btn-primary" style="margin-top:12px" @click="addPayoutRow">
              <i class="fa fa-plus"></i> Add payout
            </button>
          </div>
        </div>
      </section>

      
      <section v-if="!isMultiEditMode" class="gof-section">
        <header class="gof-section-header">
          <div class="gof-section-icon"><i class="fa fa-comment"></i></div>
          <div>
            <h2 class="gof-section-title">Comments</h2>
            <p class="gof-section-sub">Saved on every offer created from this form</p>
          </div>
        </header>
        <div class="gof-section-body">
          <div class="gof-field">
            <label class="gof-label">
              Comments
              <span class="gof-hint">Optional — internal note stored in Prime</span>
            </label>
            <textarea
              class="gof-input"
              v-model="comments"
              rows="4"
              placeholder="e.g. special payout terms, launch notes…"
              style="max-width:none;min-height:96px;resize:vertical;line-height:1.4"
            ></textarea>
          </div>
        </div>
      </section>

      
      <div class="gof-summary">
        <div style="flex-shrink:0">
          <template v-if="affiseHasSuccessfulResults">
            <button type="button" class="gof-btn-primary" @click="clearAffiseResults">Close</button>
            <p class="gof-hint" style="margin-top:8px;max-width:180px;color:#16a34a">
              <i class="fa fa-check"></i> Offers saved in Prime and Affise.
            </p>
          </template>
          <button
            type="button"
            class="gof-btn-primary"
            :style="affiseHasSuccessfulResults ? 'margin-top:8px' : ''"
            @click="openReview"
          >
            {{ isMultiEditMode ? 'Update selected' : (hasFileFlowSession ? 'Update Offer' : 'Create Offer') }}
          </button>
          <p v-if="!affiseHasSuccessfulResults" class="gof-hint" style="margin-top:8px;max-width:180px">
            <template v-if="isMultiEditMode">
              Updates <strong>{{ multiEditIds.length }}</strong> selected offer{{ multiEditIds.length === 1 ? '' : 's' }} — only filled fields are applied.
            </template>
            <template v-else>
              {{ hasFileFlowSession ? 'Updates' : 'Creates' }}
              <strong>{{ offerCount }}</strong> offer{{ offerCount > 1 ? 's' : '' }} in Prime and syncs to Affise.
            </template>
          </p>
        </div>
        <div class="gof-summary-names">
          <p class="gof-hint" style="font-weight:500;margin-bottom:8px">
            Offer name{{ offerNames.length > 1 ? `s (${offerNames.length})` : '' }}
            {{ affiseHasSuccessfulResults ? (hasFileFlowSession ? 'saved' : 'created') : 'to be synced' }}
          </p>
          <div v-for="(o, i) in offerNames" :key="'sum-' + i" class="gof-preview" style="margin-bottom:6px">
            <span v-if="o.name">{{ o.name }}</span>
            <span v-else class="gof-preview-empty">— fill in the fields —</span>
          </div>
          <div v-if="affiseHasSuccessfulResults" style="margin-top:12px">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
              <p class="gof-hint" style="font-weight:500;margin:0">Prime &amp; Affise IDs</p>
              <button type="button" class="gof-link-btn" style="margin:0" @click="copyResults">
                <i class="fa fa-copy"></i> {{ copied ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <div
              v-for="(r, i) in affiseResults"
              :key="'res-' + i"
              class="gof-preview"
              style="margin-bottom:6px;background:#f0fdf4;border-color:#bbf7d0"
            >
              <div style="display:flex;justify-content:space-between;gap:12px">
                <span>{{ r.name }}</span>
                <span style="flex-shrink:0;color:#166534">Global #{{ r.globalOfferId }}</span>
              </div>
              <div class="gof-hint" style="margin-top:2px">
                {{ r.affise.map(a => a.kind + ': Affise #' + a.id).join('  ·  ') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="gof-footer-note">Offers are stored in Prime and synced to Affise via API. Edits update existing offers until you refresh the page.</p>
    </div>

    
    <div v-if="tool === 'appsflyer'" class="gof-modal-backdrop" @click.self="tool = null">
      <div class="gof-modal gof-modal-wide">
        <header class="gof-modal-header">
          <div>
            <h2 class="gof-section-title">AppsFlyer Link Generator</h2>
            <p class="gof-section-sub">Edit fields if needed, then return the generated URLs to this offer</p>
          </div>
          <button type="button" class="gof-icon-btn" @click="tool = null"><i class="fa fa-times"></i></button>
        </header>
        <div class="gof-modal-body gof-modal-body-scroll">
          <AppsflyerLinkGeneratorCore
            ref="afGenerator"
            embedded
            :initial="afGeneratorInitial"
            :auto-generate="false"
            @can-apply="afCanApply = $event"
            @apply="onAfLinksApply"
          />
        </div>
        <footer class="gof-modal-footer">
          <button type="button" class="gof-btn-sm" @click="tool = null">Cancel</button>
          <button type="button" class="gof-btn-primary" :disabled="!afCanApply" @click="confirmAfLinks">
            <i class="fa fa-check"></i> Return links to offer
          </button>
        </footer>
      </div>
    </div>

    
    <div v-if="tool === 'adjust'" class="gof-modal-backdrop" @click.self="tool = null">
      <div class="gof-modal gof-modal-wide">
        <header class="gof-modal-header">
          <div>
            <h2 class="gof-section-title">Adjust Link Generator</h2>
            <p class="gof-section-sub">Fill Link ID and remaining fields, generate, then return the URL to this offer</p>
          </div>
          <button type="button" class="gof-icon-btn" @click="tool = null"><i class="fa fa-times"></i></button>
        </header>
        <div class="gof-modal-body gof-modal-body-scroll">
          <AdjustLinkGeneratorCore
            ref="adjustGenerator"
            embedded
            :initial="adjustGeneratorInitial"
            @can-apply="adjustCanApply = $event"
            @apply="onAdjustLinksApply"
          />
        </div>
        <footer class="gof-modal-footer">
          <button type="button" class="gof-btn-sm" @click="tool = null">Cancel</button>
          <button type="button" class="gof-btn-primary" :disabled="!adjustCanApply" @click="confirmAdjustLinks">
            <i class="fa fa-check"></i> Return link to offer
          </button>
        </footer>
      </div>
    </div>

    
    <div
      v-if="reviewOpen"
      class="gof-modal-backdrop"
      @click.self="affiseHasSuccessfulResults ? goToOffersList() : (reviewOpen = false)"
    >
      <div class="gof-modal">
        <header class="gof-modal-header">
          <div>
            <h2 class="gof-section-title">
              {{ affiseHasSuccessfulResults
                ? (affiseLastSubmitWasUpdate ? 'Offers updated' : 'Offers created')
                : (isMultiEditMode
                  ? 'Update selected offers'
                  : (hasFileFlowSession ? 'Update Affise offers' : 'Create Affise offers')) }}
            </h2>
            <p class="gof-section-sub">
              {{ affiseHasSuccessfulResults
                ? (affiseLastSubmitWasUpdate ? 'Updated in Prime and Affise' : 'Created in Prime and Affise')
                : (isMultiEditMode
                  ? 'Only filled fields are applied to each selected offer'
                  : (hasFileFlowSession ? 'Update existing offers in Prime and Affise' : 'Create offers in Prime and sync each to Affise')) }}
            </p>
          </div>
          <button
            type="button"
            class="gof-icon-btn"
            @click="affiseHasSuccessfulResults ? goToOffersList() : (reviewOpen = false)"
          >
            <i class="fa fa-times"></i>
          </button>
        </header>
        <div class="gof-modal-body">
          <div
            v-if="affiseHasSuccessfulResults && !affiseError"
            style="margin-bottom:16px;border-radius:6px;background:#f0fdf4;border:1px solid #bbf7d0;padding:12px"
          >
            <p style="font-size:0.875rem;font-weight:500;color:#166534;margin:0">
              <i class="fa fa-check"></i>
              {{ affiseLastSubmitWasUpdate ? 'Updated in Prime and Affise.' : 'Created in Prime and Affise.' }}
            </p>
          </div>
          <p v-if="affiseError" class="gof-err-text" style="margin-bottom:12px">
            <i class="fa fa-exclamation-triangle"></i> {{ affiseError }}
          </p>

          <template v-if="affiseHasSuccessfulResults">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
              <p class="gof-hint" style="font-weight:500;margin:0">
                Offer{{ affiseResults.length === 1 ? '' : 's' }} created
              </p>
              <button type="button" class="gof-link-btn" style="margin:0" @click="copyOfferNames">
                <i class="fa fa-copy"></i> {{ copiedNames ? 'Copied' : 'Copy names' }}
              </button>
            </div>
            <div
              v-for="(r, i) in affiseResults"
              :key="'created-' + i"
              class="gof-preview"
              style="margin-bottom:8px;background:#f0fdf4;border-color:#bbf7d0"
            >
              <div>{{ r.name || '—' }}</div>
              <div v-if="r.affise && r.affise.length" class="gof-hint" style="margin-top:4px">
                {{ r.affise.map(a => a.kind + ': Affise #' + a.id).join('  ·  ') }}
                <template v-if="r.globalOfferId"> · Global #{{ r.globalOfferId }}</template>
              </div>
              <div v-if="r.errors && r.errors.length" class="gof-err-text" style="margin-top:4px">
                {{ r.errors.join(' · ') }}
              </div>
            </div>
          </template>
          <template v-else>
            <div v-for="(o, i) in offerNames" :key="'rev-' + i" class="gof-preview" style="margin-bottom:8px">
              {{ o.name || '—' }}
            </div>
          </template>
        </div>
        <footer class="gof-modal-footer">
          <button
            v-if="affiseHasSuccessfulResults"
            type="button"
            class="gof-btn-primary"
            @click="goToOffersList"
          >
            Close
          </button>
          <template v-else>
            <button type="button" class="gof-btn-sm" @click="reviewOpen = false">Cancel</button>
            <button
              type="button"
              class="gof-btn-primary"
              :disabled="affiseSubmitting"
              @click="submitAffise"
            >
              <i v-if="affiseSubmitting" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-check"></i>
              {{ affiseSubmitting ? 'Sending…' : (isMultiEditMode || hasFileFlowSession ? 'Update in Affise' : 'Send to Affise') }}
            </button>
          </template>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import countries from '@/assets/countries.js'
import './create/gof-form.css'
import AppsflyerLinkGeneratorCore from '@/views/management/appsflyer-link-generator/AppsflyerLinkGeneratorCore.vue'
import AdjustLinkGeneratorCore from '@/views/management/adjust-link-generator/AdjustLinkGeneratorCore.vue'
import {
  MMPS,
  KPI_DEFS,
  TIMEFRAMES,
  CAP_SCOPES,
  PAYMENT_TYPES,
  OS_OPTIONS,
  CURRENCIES,
  ADV_CUSTOM_NUMBERS,
  AFFISE_DEVICE_TYPES,
  ADD_BUNDLE_URL,
  isAppsFlyerMmp,
  isOtherAgency,
  resolveAfAgency,
  isOneLinkAttribution,
  sanitizeCsv,
  newKpiRow,
  kpiDef,
  newCap,
  newPayoutRow,
  buildOfferNames,
  rebuildEditOfferNames,
  formatBundleInputLabel,
  clickCapsFromApi,
  clickCapsForPayload,
  buildFileFlowPayload,
  mapFileFlowResultsToAffiseDisplay,
  syncFileFlowSessionFromResults,
  payoutsFromApiToFormRows,
  copyToClipboard,
  buildOfferCopyText,
  buildOfferNamesCopyText,
} from './create/offer-form-utils'
import {
  sanitizeManualOrigin,
  extractManualOriginFromUrl,
  applyOriginOverrideToLinks,
  stripWhitespaceIfPresent,
} from '@/views/management/appsflyer-link-generator/appsflyer-link-utils'

export default {
  name: 'CreateGlobalOffer',
  components: {
    AppsflyerLinkGeneratorCore,
    AdjustLinkGeneratorCore,
  },
  data() {
    return {
      loading: true,
      loadingRefs: false,
      MMPS,
      TIMEFRAMES,
      CAP_SCOPES,
      PAYMENT_TYPES,
      OS_OPTIONS,
      CURRENCIES,
      AFFISE_DEVICE_TYPES,
      ADD_BUNDLE_URL,
      sanitizeCsv,

      advertisers: [],
      advMatches: [],
      advQuery: '',
      advOpen: false,
      advTouched: false,
      advSearching: false,
      advBrowsingAll: false,
      selectedAdvCache: null,

      bundleMatches: [],
      selectedBundleCache: null,
      bundleSearching: false,
      bundleBrowsingAll: false,

      afAccountsList: [],
      afShowOtherAccounts: false,
      afAgencies: [],
      countryOptions: [],
      countryMatches: [],
      countryQuery: '',
      countryOpen: false,
      countryTouched: false,
      countryBrowsingAll: false,

      advertiserId: '',
      advDefaultUrl: '',
      advImpDefaultUrl: '',
      comments: '',
      bundleId: '',
      bundleQuery: '',
      bundleOpen: false,
      bundleTouched: false,
      country: '',
      attribution: 'CTA',
      offerType: 'CPA',
      mmp: 'AF',
      afAccounts: [],
      afAgency: 'none',
      afAgencyOther: '',
      trackingUrl: '',
      impressionUrl: '',
      manualUrl: '',
      kpiRows: [],
      kpiPick: '',
      clickCaps: [newCap('click')],
      conversionCaps: [newCap('conversion')],
      budgetCap: newCap('budget'),
      payoutRows: [newPayoutRow({ _addN: '' })],
      expandedIds: {},
      tool: null,
      afCanApply: false,
      adjustCanApply: false,
      generatedLinks: [],
      generatedLinkEdits: {},
      offerFreeText: '',
      reviewOpen: false,
      affiseResults: [],
      affiseSubmitting: false,
      affiseError: '',
      fileFlowSessionItems: {},
      affiseLastSubmitWasUpdate: false,
      copied: false,
      copiedNames: false,
      _copiedTimer: null,
      editOfferId: null,
      duplicateFromId: null,
      editSessionOffers: [],
      multiEditIds: [],
      advUnlockedForEdit: false,
      loadingEdit: false,
      editLoadError: '',
    }
  },
  computed: {
    isEditMode() {
      return !!this.editOfferId && !this.isMultiEditMode
    },
    isDuplicateMode() {
      return !!this.duplicateFromId && !this.isEditMode && !this.isMultiEditMode
    },
    isMultiEditMode() {
      return (this.multiEditIds || []).length > 0
    },
    pageModeLabel() {
      if (this.isMultiEditMode) return 'Edit multiple'
      if (this.isEditMode) return 'Edit Offer'
      if (this.isDuplicateMode) return 'Duplicate Offer'
      return 'Create Offer'
    },
    mainAffiseIds() {
      const ids = []
      const seen = {}
      const push = (raw) => {
        if (raw == null || raw === '') return
        const id = Number(raw)
        if (!Number.isFinite(id) || id <= 0 || seen[id]) return
        seen[id] = true
        ids.push(id)
      }
      ;(this.editSessionOffers || []).forEach((o) => push(o && o.affise_offer_id))
      Object.keys(this.fileFlowSessionItems || {}).forEach((key) => {
        const item = this.fileFlowSessionItems[key]
        push(item && item.affise_offer_id)
      })
      return ids
    },
    selectedAdv() {
      if (this.selectedAdvCache && String(this.selectedAdvCache.id) === String(this.advertiserId)) {
        return this.selectedAdvCache
      }
      return this.advMatches.find((a) => String(a.id) === String(this.advertiserId)) || null
    },
    bundle() {
      const raw =
        (this.selectedBundleCache && this.selectedBundleCache.bundle_id === this.bundleId
          ? this.selectedBundleCache
          : null) ||
        this.bundleMatches.find((b) => b.bundle_id === this.bundleId) ||
        null
      if (!raw) return null
      return {
        app: raw.app_name,
        os: raw.os,
        vertical: raw.vertical_name,
        mmp: raw.mmp,
      }
    },
    isAppsFlyer() {
      return isAppsFlyerMmp(this.mmp)
    },
    usesLinkGenerator() {
      return this.isAppsFlyer || this.mmp === 'Adjust'
    },
    isVTA() {
      return this.attribution === 'VTA'
    },
    isOneLink() {
      return isOneLinkAttribution(this.attribution)
    },
    mmpLabel() {
      const m = this.MMPS.find((x) => x.value === this.mmp)
      return (m && m.label) || this.mmp || ''
    },
    isOtherAgency() {
      return isOtherAgency(this.afAgency)
    },
    resolvedAfAgency() {
      return resolveAfAgency(this.afAgency, this.afAgencyOther)
    },
    afAgenciesForSelect() {
      return (this.afAgencies || []).filter(
        (a) => !isOtherAgency(a.label) && !isOtherAgency(a.value)
      )
    },
    offerCount() {
      return this.offerNames.length || 1
    },
    hasFileFlowSession() {
      return Object.keys(this.fileFlowSessionItems).length > 0
    },
    generatedLinkUrlCount() {
      return (this.generatedLinks || []).reduce(
        (n, l) => n + (l && l.click ? 1 : 0) + (l && l.impression ? 1 : 0),
        0
      )
    },
    affiseHasSuccessfulResults() {
      return (this.affiseResults || []).some((r) => (r.affise || []).length > 0)
    },
    offerNames() {
      if (this.isMultiEditMode && this.editSessionOffers.length) {
        return this.editSessionOffers.map((o) => ({
          account: o.account || '',
          variant: o.variant || 'CTA',
          name: o.title || '',
        }))
      }
      const advForName = this.selectedAdv
        ? {
            name: this.selectedAdv.company || this.selectedAdv.name,
            company: this.selectedAdv.company || this.selectedAdv.name,
          }
        : null
      const built = buildOfferNames({
        bundle: this.bundle,
        selectedAdv: advForName,
        mmp: this.mmp,
        afAgency: this.resolvedAfAgency,
        afAccounts: this.afAccounts,
        attribution: this.attribution,
        country: this.country,
        freeText: this.offerFreeText,
        offerType: this.offerType,
      })
      if (this.isEditMode && this.editSessionOffers.length) {
        return rebuildEditOfferNames(built, this.editSessionOffers)
      }
      return built
    },
    bundleNoMatch() {
      return this.bundleQuery.trim() !== '' && !this.bundleSearching && this.bundleMatches.length === 0
    },
    showAdvDropdown() {
      return this.advOpen && (this.advSearching || this.advMatches.length > 0 || this.advQuery.trim().length > 0 || this.advBrowsingAll)
    },
    showBundleDropdown() {
      return (
        this.bundleOpen &&
        !this.bundle &&
        (this.bundleSearching || this.bundleMatches.length > 0 || this.bundleQuery.trim().length > 0 || this.bundleBrowsingAll)
      )
    },
    showCountryDropdown() {
      return this.countryOpen && (this.countryMatches.length > 0 || this.countryQuery.trim().length > 0 || this.countryBrowsingAll)
    },
    afOnoAccounts() {
      return this.afAccountsList.filter((a) => a.ono)
    },
    afOtherAccounts() {
      return this.afAccountsList.filter((a) => !a.ono)
    },
    afGeneratorInitial() {
      const osRaw = ((this.bundle && this.bundle.os) || '').toLowerCase()
      const os = osRaw === 'ios' ? 'ios' : osRaw === 'android' ? 'android' : 'android'
      const selectedPids = (this.afAccounts || [])
        .map((label) => {
          const hit = this.afAccountsList.find((a) => a.label === label)
          return hit ? hit.value : null
        })
        .filter(Boolean)
      let agency = ''
      if (this.isOtherAgency) {
        agency = this.resolvedAfAgency
      } else if (this.afAgency && this.afAgency !== 'none') {
        const hit = this.afAgencies.find((a) => a.label === this.afAgency)
        agency = (hit && hit.value) || this.afAgency
      }
      return {
        linkType: this.isVTA ? 'cta_vta' : 'cta',
        os,
        bundleId: this.bundleId || '',
        agency,
        selectedPids,
        costModel: String(this.offerType || 'CPA').toLowerCase() === 'cpi' ? 'cpi' : 'cpa',
        originOverride: this.isOneLink ? this.manualUrl : '',
      }
    },
    adjustGeneratorInitial() {
      const osRaw = ((this.bundle && this.bundle.os) || '').toLowerCase()
      const os = osRaw === 'ios' ? 'ios' : 'android'
      return {
        
        type: this.isVTA ? 'https://view.adjust.com/impression/' : 'https://app.adjust.com/',
        os,
        cost_type: String(this.offerType || 'CPA').toLowerCase() === 'cpi' ? 'cpi' : 'cpa',
        originOverride: this.isOneLink ? this.manualUrl : '',
      }
    },
    kpiGroups() {
      return ['CR', 'Retention Rate', null]
    },
  },
  watch: {
    mmp() {
      if (!this.usesLinkGenerator) {
        this.syncManualTrackingLinks()
      }
    },
    attribution() {
      if (!this.usesLinkGenerator) {
        if (!this.isVTA) this.impressionUrl = ''
        this.syncManualTrackingLinks()
      } else if (this.isOneLink) {
        this.applyManualOriginToCurrentLinks()
      }
    },
    '$route.query.id'() {
      this.bootstrapFromRoute()
    },
    '$route.query.ids'() {
      this.bootstrapFromRoute()
    },
    '$route.query.duplicateFrom'() {
      this.bootstrapFromRoute()
    },
  },
  created() {
    this.countryOptions = Object.keys(countries).map((code) => ({
      value: code,
      label: `${code} — ${countries[code]}`,
    }))
    
    this._advSearchSeq = 0
    this._bundleSearchSeq = 0
    this._advTimer = null
    this._bundleTimer = null
    document.addEventListener('mousedown', this.onDocClick)
    this.loading = false
    this.loadReferenceData().then(() => this.bootstrapFromRoute())
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocClick)
    if (this._advTimer) clearTimeout(this._advTimer)
    if (this._bundleTimer) clearTimeout(this._bundleTimer)
    if (this._copiedTimer) clearTimeout(this._copiedTimer)
  },
  methods: {
    async loadReferenceData() {
      this.loadingRefs = true
      const settle = async (label, promise, apply) => {
        try {
          const res = await promise
          apply(res)
        } catch (err) {
          console.error(`Failed to load ${label}`, err)
        }
      }

      await Promise.all([
        settle('af accounts', this.$http.post(this.resources.Management.getPid, {}), (pidRes) => {
          const pidData = (pidRes.body && pidRes.body.data) || []
          this.afAccountsList = pidData
            .filter((p) => p.pid_text && p.pid_text !== 'Other')
            .map((p) => ({
              label: `${p.pid_text} (${p.pid_value})`,
              value: p.pid_value,
              id: p.id,
              ono: p.ono === 1 || p.ono === true,
            }))
        }),
        settle('af agencies', this.$http.post(this.resources.Management.getAfPart, {}), (afPartRes) => {
          const afPartData = (afPartRes.body && afPartRes.body.data) || []
          this.afAgencies = afPartData.map((a) => ({
            label: a.af_part_text,
            value: a.af_part_value,
          }))
        }),
      ])

      this.loadingRefs = false
    },
    bootstrapFromRoute() {
      const query = (this.$route && this.$route.query) || {}
      const rawIds = query.ids != null ? String(query.ids).trim() : ''
      const multiIds = rawIds
        ? rawIds.split(',').map((s) => s.trim()).filter((s) => s && /^\d+$/.test(s))
        : []
      const rawId = query.id != null && String(query.id).trim() !== '' ? String(query.id).trim() : ''
      const rawDup =
        query.duplicateFrom != null && String(query.duplicateFrom).trim() !== ''
          ? String(query.duplicateFrom).trim()
          : ''

      if (multiIds.length) {
        const key = multiIds.join(',')
        if (this.multiEditIds.join(',') === key && this.editSessionOffers.length) return
        this.editOfferId = null
        this.duplicateFromId = null
        this.loadMultiEditOffers(multiIds)
        return
      }

      this.multiEditIds = []

      if (rawDup) {
        if (
          String(this.duplicateFromId || '') === rawDup &&
          !this.editOfferId &&
          this.advertiserId &&
          this.bundleId
        ) {
          return
        }
        this.editOfferId = null
        this.loadDuplicateOffer(rawDup)
        return
      }

      const wasDuplicate = !!this.duplicateFromId
      this.duplicateFromId = null
      if (!rawId) {
        if (this.editOfferId || this.editSessionOffers.length || wasDuplicate) {
          this.editOfferId = null
          this.duplicateFromId = null
          this.editSessionOffers = []
          this.advUnlockedForEdit = false
          this.editLoadError = ''
          this.fileFlowSessionItems = {}
          this.resetForm()
        }
        return
      }
      if (String(this.editOfferId || '') === rawId && this.editSessionOffers.length) return
      this.loadEditOffer(rawId)
    },
    async loadMultiEditOffers(ids) {
      this.loadingEdit = true
      this.editLoadError = ''
      this.multiEditIds = ids.slice()
      this.editOfferId = null
      this.advUnlockedForEdit = false
      try {
        const res = await this.$http.get(this.resources.GlobalOffers.getNewGlobalOffersByIds, {
          params: { ids: ids.join(','), _t: Date.now() },
        })
        let data = res.body
        if (typeof data === 'string') data = JSON.parse(data)
        else if (!data || data.res == null) data = JSON.parse(res.bodyText || '{}')
        if (!data || data.res !== 'OK') {
          this.editLoadError = (data && data.data) || 'Failed to load offers'
          return
        }
        const items = (data.data && data.data.items) || []
        if (!items.length) {
          this.editLoadError = 'No offers found for selection'
          return
        }
        const first = items[0]
        const sameBundleAdv = items.every(
          (o) =>
            String(o.bundle_id || '') === String(first.bundle_id || '') &&
            String(o.advertiser_id || '') === String(first.advertiser_id || '')
        )
        if (!sameBundleAdv) {
          this.editLoadError = 'Edit multiple requires the same Bundle and Advertiser for all selected offers'
          this.editSessionOffers = []
          return
        }
        this.editSessionOffers = items
        this.prepareMultiEditForm()
      } catch (err) {
        console.error('getNewGlobalOffersByIds failed', err)
        this.editLoadError =
          (err && err.body && err.body.data) ||
          (err && err.bodyText) ||
          (err && err.message) ||
          'Failed to load offers'
      } finally {
        this.loadingEdit = false
      }
    },
    prepareMultiEditForm() {
      this.clearAdvertiser()
      this.clearBundle()
      this.clearCountry()
      this.attribution = 'CTA'
      this.offerType = ''
      this.mmp = 'AF'
      this.afAccounts = []
      this.afAgency = 'none'
      this.afAgencyOther = ''
      this.advDefaultUrl = ''
      this.advImpDefaultUrl = ''
      this.trackingUrl = ''
      this.impressionUrl = ''
      this.manualUrl = ''
      this.generatedLinks = []
      this.generatedLinkEdits = {}
      this.clickCaps = [newCap('click')]
      this.payoutRows = [newPayoutRow({ _addN: '' })]
      this.offerFreeText = ''
      this.affiseResults = []
      this.affiseError = ''
      this.reviewOpen = false
      this.fileFlowSessionItems = {}
    },
    async loadEditOffer(id) {
      this.loadingEdit = true
      this.editLoadError = ''
      this.editOfferId = id
      this.duplicateFromId = null
      this.multiEditIds = []
      this.advUnlockedForEdit = false
      try {
        const res = await this.$http.get(this.resources.GlobalOffers.getNewGlobalOffer, {
          params: { id, _t: Date.now() },
        })
        let data = res.body
        if (typeof data === 'string') data = JSON.parse(data)
        else if (!data || data.res == null) data = JSON.parse(res.bodyText || '{}')
        if (!data || data.res !== 'OK') {
          this.editLoadError = (data && data.data) || 'Failed to load offer'
          return
        }
        const payload = data.data || {}
        const items = payload.items && payload.items.length ? payload.items : (payload.offer ? [payload.offer] : [])
        if (!items.length) {
          this.editLoadError = 'Offer not found'
          return
        }
        this.applyEditPayload(payload.offer || items[0], items, payload.bundle || null)
      } catch (err) {
        console.error('getNewGlobalOffer failed', err)
        this.editLoadError =
          (err && err.body && err.body.data) ||
          (err && err.bodyText) ||
          (err && err.message) ||
          'Failed to load offer'
      } finally {
        this.loadingEdit = false
      }
    },
    async loadDuplicateOffer(id) {
      this.loadingEdit = true
      this.editLoadError = ''
      this.editOfferId = null
      this.duplicateFromId = String(id)
      this.multiEditIds = []
      this.advUnlockedForEdit = false
      this.fileFlowSessionItems = {}
      try {
        const res = await this.$http.get(this.resources.GlobalOffers.getNewGlobalOffer, {
          params: { id, _t: Date.now() },
        })
        let data = res.body
        if (typeof data === 'string') data = JSON.parse(data)
        else if (!data || data.res == null) data = JSON.parse(res.bodyText || '{}')
        if (!data || data.res !== 'OK') {
          this.editLoadError = (data && data.data) || 'Failed to load offer to duplicate'
          return
        }
        const payload = data.data || {}
        const items = payload.items && payload.items.length ? payload.items : (payload.offer ? [payload.offer] : [])
        if (!items.length) {
          this.editLoadError = 'Offer not found'
          return
        }
        this.applyDuplicatePayload(payload.offer || items[0], items, payload.bundle || null)
      } catch (err) {
        console.error('loadDuplicateOffer failed', err)
        this.editLoadError =
          (err && err.body && err.body.data) ||
          (err && err.bodyText) ||
          (err && err.message) ||
          'Failed to load offer to duplicate'
      } finally {
        this.loadingEdit = false
      }
    },
    applyDuplicatePayload(primary, items, bundleRow) {
      this.applyEditPayload(primary, items, bundleRow)
      
      this.duplicateFromId = String((primary && primary.id) || this.duplicateFromId || '')
      this.editOfferId = null
      this.editSessionOffers = []
      this.fileFlowSessionItems = {}
      this.advUnlockedForEdit = false
      this.affiseResults = []
      this.affiseError = ''
      this.reviewOpen = false
      this.payoutRows = (this.payoutRows || []).map((row) =>
        Object.assign({}, row, { affisePaymentId: '' })
      )
    },
    applyEditPayload(primary, items, bundleRow) {
      const cta = items.find((o) => String(o.variant || '') === 'CTA') || primary
      const vta = items.find((o) => String(o.variant || '').toLowerCase().indexOf('vta') !== -1) || null
      this.editSessionOffers = items.slice()
      this.editOfferId = String(primary.id || cta.id)

      this.attribution = cta.attribution || primary.attribution || 'CTA'
      this.offerType = cta.offer_type || 'CPA'
      this.mmp = cta.mmp === 'AppsFlyer' ? 'AF' : (cta.mmp || 'AF')
      this.country = cta.country || ''
      if (this.country) {
        const c = this.countryOptions.find((x) => x.value === this.country)
        this.countryQuery = c ? c.label : this.country
        this.countryMatches = c ? [c] : []
      } else {
        this.clearCountry()
      }

      this.advDefaultUrl = cta.advertiser_default_url || ''
      this.comments = cta.comments || primary.comments || ''
      this.agencyFromEdit(cta.agency)
      this.accountsFromEdit(cta.account)

      const caps = cta.click_caps
      this.clickCaps = clickCapsFromApi(caps)
      this.payoutRows = payoutsFromApiToFormRows(cta.payouts)

      
      this.trackingUrl = (cta && cta.tracking_url) || ''
      this.impressionUrl = (vta && vta.tracking_url) || ''
      this.manualUrl = this.isOneLink
        ? extractManualOriginFromUrl(this.trackingUrl || this.impressionUrl)
        : ''
      const byAccount = {}
      items.forEach((o) => {
        const key = o.account || 'Adjust'
        if (!byAccount[key]) byAccount[key] = { account: key, click: '', impression: null }
        if (String(o.variant || '') === 'CTA') byAccount[key].click = o.tracking_url || ''
        else byAccount[key].impression = o.tracking_url || null
      })
      this.generatedLinks = Object.keys(byAccount).map((k) => byAccount[k])
      if (!this.generatedLinks.length && (this.trackingUrl || this.impressionUrl)) {
        this.generatedLinks = [{
          account: cta.account || this.mmpLabel || 'Manual',
          click: this.trackingUrl || '',
          impression: this.impressionUrl || null,
        }]
      }
      this.resetGeneratedLinkEdits()

      
      const session = {}
      items.forEach((o) => {
        if (!o.id || !o.affise_offer_id) return
        session[`${o.account || ''}::${o.variant || ''}`] = {
          id: o.id,
          affise_offer_id: o.affise_offer_id,
        }
      })
      this.fileFlowSessionItems = session

      
      if (bundleRow && bundleRow.bundle_id) {
        this.bundleId = bundleRow.bundle_id
        this.selectedBundleCache = {
          bundle_id: bundleRow.bundle_id,
          app_name: bundleRow.app_name || '',
          os: bundleRow.os || '',
          vertical_name: bundleRow.vertical_name || '',
          mmp: cta.mmp || '',
        }
        this.bundleMatches = [this.selectedBundleCache]
        this.bundleQuery = formatBundleInputLabel(this.selectedBundleCache) || bundleRow.bundle_id
        if (!bundleRow.app_name) {
          this.hydrateBundle(bundleRow.bundle_id)
        }
      } else if (cta.bundle_id) {
        this.bundleId = cta.bundle_id
        this.bundleQuery = cta.bundle_id
        this.hydrateBundle(cta.bundle_id)
      }

      const advertiserName =
        cta.advertiser_name ||
        primary.advertiser_name ||
        ''
      this.hydrateAdvertiser(cta.advertiser_id, cta.advertiser_affise_id, advertiserName)
      this.offerFreeText = ''
      this.affiseResults = []
      this.affiseError = ''
      this.reviewOpen = false
    },
    agencyFromEdit(agency) {
      const val = String(agency || '').trim()
      this.afAgencyOther = ''
      if (!val) {
        this.afAgency = 'none'
        return
      }
      if (isOtherAgency(val)) {
        this.afAgency = 'Other'
        return
      }
      const hit = this.afAgencies.find(
        (a) => a.label === val || a.value === val || String(a.label).toLowerCase() === val.toLowerCase()
      )
      if (hit) {
        this.afAgency = hit.label
        return
      }
      this.afAgency = 'Other'
      this.afAgencyOther = val
    },
    accountsFromEdit(account) {
      const raw = String(account || '').trim()
      if (!raw || !this.isAppsFlyer) {
        this.afAccounts = raw && this.isAppsFlyer ? [raw] : []
        return
      }
      const short = raw.split(' (')[0].trim().toLowerCase()
      const hit = this.afAccountsList.find((a) => {
        const label = String(a.label || '')
        return (
          label === raw ||
          label.toLowerCase() === raw.toLowerCase() ||
          label.split(' (')[0].trim().toLowerCase() === short
        )
      })
      this.afAccounts = [hit ? hit.label : raw]
      if (hit && !hit.ono) this.afShowOtherAccounts = true
    },
    async hydrateAdvertiser(advertiserId, affiseAdvId, advertiserNameHint) {
      const id = String(advertiserId || '').trim()
      const affiseId = String(affiseAdvId || '').trim()
      const nameHint = String(advertiserNameHint || '').trim()
      if (!id && !affiseId) return

      const applyAdv = (hit) => {
        if (!hit) return
        this.advertiserId = hit.id
        this.selectedAdvCache = hit
        this.advQuery = hit.name
        this.advMatches = [hit]
      }

      const stubName = nameHint || (id ? `Advertiser ${id}` : `Affise ${affiseId}`)
      applyAdv({
        id: id || affiseId,
        name: stubName,
        company: nameHint || stubName,
        affiseAdvId: affiseId,
      })

      try {
        if (id) {
          const res = await this.$http.post(this.resources.Management.getAdvertisers(id))
          const body = (res && res.body) || {}
          if (body.res === 'OK' && body.data) {
            applyAdv(this.mapAdvertiser({ ...body.data, id: body.data.id || id }))
            return
          }
        }
        const q = nameHint || id || affiseId
        const res = await this.$http.post(this.resources.Management.searchAdvertisers, { q })
        const rows = ((res && res.body && res.body.data) || []).map((a) => this.mapAdvertiser(a))
        const hit =
          rows.find((a) => String(a.id) === String(id)) ||
          rows.find((a) => String(a.affiseAdvId) === String(affiseId)) ||
          (nameHint
            ? rows.find((a) => String(a.company || '').toLowerCase() === nameHint.toLowerCase())
            : null) ||
          rows[0]
        if (hit) applyAdv(hit)
      } catch (err) {
        console.error('hydrateAdvertiser', err)
      }
    },
    async hydrateBundle(bundleId) {
      const q = String(bundleId || '').trim()
      if (!q) return
      try {
        const res = await this.$http.post(this.resources.BundleIDs.searchBundles, { q })
        const rows = (res && res.body && res.body.data) || []
        const hit = rows.find((b) => b.bundle_id === q) || rows[0]
        if (hit) {
          this.selectedBundleCache = hit
          this.bundleMatches = [hit]
          this.bundleId = hit.bundle_id
          this.bundleQuery = formatBundleInputLabel(hit) || hit.bundle_id
        }
      } catch (err) {
        console.error('hydrateBundle', err)
      }
    },
    requestAdvUnlock() {
      if (!this.isEditMode) return
      const first = window.confirm(
        'Change advertiser for this offer?\n\nThis is unusual and can break Affise mapping. Continue?'
      )
      if (!first) return
      const second = window.confirm(
        'Final confirmation: unlock advertiser editing for this offer?'
      )
      if (!second) return
      this.advUnlockedForEdit = true
      this.advOpen = true
    },
    mapAdvertiser(a) {
      return {
        id: a.id,
        name: a.affise_adv_name
          ? `${a.adv_company_name} - ${a.affise_adv_name}`
          : a.adv_company_name,
        company: a.adv_company_name,
        affiseAdvId: a.affise_adv_id || '',
        affiseAdvName: a.affise_adv_name || '',
      }
    },
    onDocClick(e) {
      if (this.$refs.bundleBox && !this.$refs.bundleBox.contains(e.target)) {
        this.bundleOpen = false
      }
      if (this.$refs.advBox && !this.$refs.advBox.contains(e.target)) {
        this.advOpen = false
      }
      if (this.$refs.countryBox && !this.$refs.countryBox.contains(e.target)) {
        this.countryOpen = false
      }
      this.payoutRows.forEach((row) => {
        if (!row._countryOpen && !row._cityOpen) return
        const countryEl = e.target && e.target.closest ? e.target.closest('.gof-payout-country-combo') : null
        const cityEl = e.target && e.target.closest ? e.target.closest('.gof-payout-city-combo') : null
        if (row._countryOpen && !countryEl) this.$set(row, '_countryOpen', false)
        if (row._cityOpen && !cityEl) this.$set(row, '_cityOpen', false)
      })
    },
    filterCountries(q) {
      const needle = String(q || '').trim().toLowerCase()
      if (!needle) return this.countryOptions.slice()
      return this.countryOptions.filter((c) => {
        return (
          String(c.value).toLowerCase().includes(needle) ||
          String(c.label).toLowerCase().includes(needle)
        )
      })
    },
    handleCountryInput(e) {
      this.countryQuery = e.target.value
      this.countryTouched = true
      this.countryBrowsingAll = false
      this.countryOpen = true
      if (this.country) this.country = ''
      const q = this.countryQuery.trim()
      if (!q) {
        this.countryMatches = []
        return
      }
      this.countryMatches = this.filterCountries(q)
    },
    openPayoutCountry(row) {
      this.$set(row, '_countryOpen', true)
    },
    handlePayoutCountryInput(row, e) {
      const q = e.target.value
      this.$set(row, '_countryQuery', q)
      this.$set(row, '_countryTouched', true)
      this.$set(row, '_countryBrowsingAll', false)
      this.$set(row, '_countryOpen', true)
      if (row.country) {
        this.$set(row, 'country', '')
        this.$set(row, 'cities', [])
        this.clearPayoutCityQuery(row)
      }
      if (!String(q || '').trim()) {
        this.$set(row, '_countryMatches', [])
        return
      }
      this.$set(row, '_countryMatches', this.filterCountries(q))
    },
    togglePayoutCountryFullList(row) {
      if (String(row._countryQuery || '').trim()) return
      if (row._countryOpen && row._countryBrowsingAll) {
        this.$set(row, '_countryOpen', false)
        return
      }
      this.$set(row, '_countryTouched', true)
      this.$set(row, '_countryBrowsingAll', true)
      this.$set(row, '_countryMatches', this.filterCountries(''))
      this.$set(row, '_countryOpen', true)
    },
    pickPayoutCountry(row, c) {
      this.$set(row, 'country', c.value)
      this.$set(row, '_countryQuery', c.label)
      this.$set(row, '_countryMatches', [c])
      this.$set(row, '_countryBrowsingAll', false)
      this.$set(row, '_countryOpen', false)
      
      this.$set(row, 'cities', [])
      this.clearPayoutCityQuery(row)
    },
    clearPayoutCountry(row) {
      this.$set(row, 'country', '')
      this.$set(row, '_countryQuery', '')
      this.$set(row, '_countryTouched', false)
      this.$set(row, '_countryMatches', [])
      this.$set(row, '_countryBrowsingAll', false)
      this.$set(row, '_countryOpen', false)
      this.$set(row, 'cities', [])
      this.clearPayoutCityQuery(row)
    },
    openPayoutCity(row) {
      if (!row.country) return
      this.$set(row, '_cityOpen', true)
    },
    clearPayoutCityQuery(row) {
      this.$set(row, '_cityQuery', '')
      this.$set(row, '_cityMatches', [])
      this.$set(row, '_cityOpen', false)
      this.$set(row, '_citySearching', false)
      if (row._cityTimer) {
        clearTimeout(row._cityTimer)
        this.$set(row, '_cityTimer', null)
      }
    },
    handlePayoutCityInput(row, e) {
      if (!row.country) return
      const q = e.target.value
      this.$set(row, '_cityQuery', q)
      this.$set(row, '_cityOpen', true)
      if (row._cityTimer) clearTimeout(row._cityTimer)
      const timer = setTimeout(() => {
        this.runPayoutCitySearch(row)
      }, 300)
      this.$set(row, '_cityTimer', timer)
    },
    async runPayoutCitySearch(row) {
      const q = String(row._cityQuery || '').trim()
      if (!row.country || !q) {
        this.$set(row, '_cityMatches', [])
        this.$set(row, '_citySearching', false)
        return
      }
      const seq = (row._citySearchSeq || 0) + 1
      this.$set(row, '_citySearchSeq', seq)
      this.$set(row, '_citySearching', true)
      try {
        const res = await this.$http.post(this.resources.GlobalOffers.searchAffiseCities, {
          country: row.country,
          q,
        })
        if (seq !== row._citySearchSeq) return
        const data = res.body || JSON.parse(res.bodyText || '{}')
        const cities = (data && data.res === 'OK' && data.data && data.data.cities) || []
        const selected = new Set((row.cities || []).map((c) => String(c.id)))
        this.$set(
          row,
          '_cityMatches',
          cities.filter((c) => !selected.has(String(c.id)))
        )
      } catch (err) {
        if (seq !== row._citySearchSeq) return
        console.error('searchAffiseCities failed', err)
        this.$set(row, '_cityMatches', [])
      } finally {
        if (seq === row._citySearchSeq) this.$set(row, '_citySearching', false)
      }
    },
    pickPayoutCity(row, c) {
      const next = (row.cities || []).slice()
      if (!next.some((x) => String(x.id) === String(c.id))) {
        next.push({ id: c.id, name: c.name })
      }
      this.$set(row, 'cities', next)
      this.clearPayoutCityQuery(row)
    },
    removePayoutCity(row, id) {
      this.$set(
        row,
        'cities',
        (row.cities || []).filter((c) => String(c.id) !== String(id))
      )
    },
    togglePayoutDevice(row, device) {
      const next = (row.devices || []).slice()
      const idx = next.indexOf(device)
      if (idx === -1) next.push(device)
      else next.splice(idx, 1)
      this.$set(row, 'devices', next)
    },
    toggleCountryFullList() {
      if (this.countryQuery.trim()) return
      if (this.countryOpen && this.countryBrowsingAll) {
        this.countryOpen = false
        return
      }
      this.countryTouched = true
      this.countryBrowsingAll = true
      this.countryMatches = this.filterCountries('')
      this.countryOpen = true
    },
    pickCountry(c) {
      this.country = c.value
      this.countryQuery = c.label
      this.countryMatches = [c]
      this.countryBrowsingAll = false
      this.countryOpen = false
    },
    clearCountry() {
      this.country = ''
      this.countryQuery = ''
      this.countryTouched = false
      this.countryMatches = []
      this.countryBrowsingAll = false
      this.countryOpen = false
    },
    handleAdvInput(e) {
      this.advQuery = e.target.value
      this.advTouched = true
      this.advBrowsingAll = false
      this.advOpen = true
      if (this.advertiserId) {
        this.advertiserId = ''
        this.selectedAdvCache = null
        this.advDefaultUrl = ''
        this.advImpDefaultUrl = ''
      }
      if (this._advTimer) clearTimeout(this._advTimer)
      this._advSearchSeq += 1
      const q = this.advQuery.trim()
      if (!q) {
        this.advSearching = false
        this.advMatches = []
        return
      }
      this.advSearching = true
      this.advMatches = []
      const seq = this._advSearchSeq
      this._advTimer = setTimeout(() => {
        this.runAdvertiserSearch(q, seq)
      }, 300)
    },
    async runAdvertiserSearch(q, seq) {
      if (seq !== this._advSearchSeq) return
      this.advSearching = true
      this.advOpen = true
      try {
        const res = await this.$http.post(this.resources.Management.searchAdvertisers, { q })
        if (seq !== this._advSearchSeq) return
        const rows = (res && res.body && res.body.data) || []
        this.advMatches = rows.map((a) => this.mapAdvertiser(a))
      } catch (err) {
        console.error('searchAdvertisers', err)
        if (seq !== this._advSearchSeq) return
        this.advMatches = []
      } finally {
        if (seq === this._advSearchSeq) {
          this.advSearching = false
          this.advOpen = true
        }
      }
    },
    toggleAdvFullList() {
      if (this.advQuery.trim()) return
      if (this.advOpen && this.advBrowsingAll && !this.advSearching) {
        this.advOpen = false
        return
      }
      this.fetchAllAdvertisers()
    },
    async fetchAllAdvertisers() {
      if (this.advQuery.trim()) return
      if (this._advTimer) clearTimeout(this._advTimer)
      this._advSearchSeq += 1
      const seq = this._advSearchSeq
      this.advTouched = true
      this.advBrowsingAll = true
      this.advSearching = true
      this.advMatches = []
      this.advOpen = true
      try {
        const res = await this.$http.post(this.resources.Management.getAdvertisersList)
        if (seq !== this._advSearchSeq) return
        const rows = (res && res.body && res.body.data) || []
        this.advMatches = rows.map((a) => this.mapAdvertiser(a))
      } catch (err) {
        console.error('getAdvertisersList', err)
        if (seq !== this._advSearchSeq) return
        this.advMatches = []
      } finally {
        if (seq === this._advSearchSeq) {
          this.advSearching = false
          this.advOpen = true
        }
      }
    },
    pickAdvertiser(a) {
      if (this._advTimer) clearTimeout(this._advTimer)
      this._advSearchSeq += 1
      this.advertiserId = a.id
      this.selectedAdvCache = a
      this.advQuery = a.name
      this.advMatches = [a]
      this.advSearching = false
      this.advBrowsingAll = false
      this.advOpen = false
      this.advDefaultUrl = ''
      this.advImpDefaultUrl = ''
      if (this.isEditMode) this.advUnlockedForEdit = false
    },
    clearAdvertiser() {
      if (this._advTimer) clearTimeout(this._advTimer)
      this._advSearchSeq += 1
      this.advQuery = ''
      this.advTouched = false
      this.advMatches = []
      this.advSearching = false
      this.advBrowsingAll = false
      this.advertiserId = ''
      this.selectedAdvCache = null
      this.advDefaultUrl = ''
      this.advImpDefaultUrl = ''
    },
    handleBundleInput(e) {
      this.bundleQuery = e.target.value
      this.bundleTouched = true
      this.bundleBrowsingAll = false
      this.bundleOpen = true
      if (this.bundleId) {
        this.bundleId = ''
        this.selectedBundleCache = null
      }
      if (this._bundleTimer) clearTimeout(this._bundleTimer)
      this._bundleSearchSeq += 1
      const q = this.bundleQuery.trim()
      if (!q) {
        this.bundleSearching = false
        this.bundleMatches = []
        return
      }
      this.bundleSearching = true
      this.bundleMatches = []
      const seq = this._bundleSearchSeq
      this._bundleTimer = setTimeout(() => {
        this.runBundleSearch(q, seq)
      }, 300)
    },
    async runBundleSearch(q, seq) {
      if (seq !== this._bundleSearchSeq) return
      this.bundleSearching = true
      this.bundleOpen = true
      try {
        const res = await this.$http.post(this.resources.BundleIDs.searchBundles, { q })
        if (seq !== this._bundleSearchSeq) return
        const rows = (res && res.body && res.body.data) || []
        this.bundleMatches = rows
        const exact = rows.find((b) => b.bundle_id === q)
        if (exact) this.pickBundle(exact)
        else if (rows.length === 1) this.pickBundle(rows[0])
      } catch (err) {
        console.error('searchBundles', err)
        if (seq !== this._bundleSearchSeq) return
        this.bundleMatches = []
      } finally {
        if (seq === this._bundleSearchSeq) {
          this.bundleSearching = false
          this.bundleOpen = true
        }
      }
    },
    toggleBundleFullList() {
      if (this.bundleQuery.trim() || this.bundle) return
      if (this.bundleOpen && this.bundleBrowsingAll && !this.bundleSearching) {
        this.bundleOpen = false
        return
      }
      this.fetchAllBundles()
    },
    async fetchAllBundles() {
      if (this.bundleQuery.trim() || this.bundle) return
      if (this._bundleTimer) clearTimeout(this._bundleTimer)
      this._bundleSearchSeq += 1
      const seq = this._bundleSearchSeq
      this.bundleTouched = true
      this.bundleBrowsingAll = true
      this.bundleSearching = true
      this.bundleMatches = []
      this.bundleOpen = true
      try {
        const res = await this.$http.post(this.resources.BundleIDs.getBundleList)
        if (seq !== this._bundleSearchSeq) return
        const rows = (res && res.body && res.body.data) || []
        this.bundleMatches = rows
      } catch (err) {
        console.error('getBundleList', err)
        if (seq !== this._bundleSearchSeq) return
        this.bundleMatches = []
      } finally {
        if (seq === this._bundleSearchSeq) {
          this.bundleSearching = false
          this.bundleOpen = true
        }
      }
    },
    pickBundle(b) {
      if (this._bundleTimer) clearTimeout(this._bundleTimer)
      this._bundleSearchSeq += 1
      this.bundleId = b.bundle_id
      this.selectedBundleCache = b
      this.bundleQuery = formatBundleInputLabel(b) || b.bundle_id
      this.bundleMatches = [b]
      this.bundleSearching = false
      this.bundleBrowsingAll = false
      this.bundleOpen = false
      if (b.mmp != null && b.mmp !== '') {
        const raw = b.mmp
        if (raw === 'AppsFlyer') this.mmp = 'AF'
        else this.mmp = raw
      }
    },
    clearBundle() {
      if (this._bundleTimer) clearTimeout(this._bundleTimer)
      this._bundleSearchSeq += 1
      this.bundleQuery = ''
      this.bundleTouched = false
      this.bundleId = ''
      this.selectedBundleCache = null
      this.bundleMatches = []
      this.bundleSearching = false
      this.bundleBrowsingAll = false
    },
    toggleAccount(label) {
      const idx = this.afAccounts.indexOf(label)
      if (idx === -1) this.afAccounts.push(label)
      else this.afAccounts.splice(idx, 1)
    },
    kpiLabel(key) {
      const d = kpiDef(key)
      return d ? d.label : key
    },
    canSplitKpi(row) {
      const d = kpiDef(row.key)
      return this.isVTA && d && d.splittable
    },
    availableKpis(group) {
      const added = new Set(this.kpiRows.map((r) => r.key))
      return KPI_DEFS.filter((d) => d.group === group && !added.has(d.key))
    },
    onAddKpi() {
      if (!this.kpiPick) return
      this.kpiRows.push(newKpiRow(this.kpiPick))
      this.kpiPick = ''
    },
    removeKpi(id) {
      this.kpiRows = this.kpiRows.filter((r) => r.id !== id)
    },
    addClickCap() {
      this.clickCaps.push(newCap('click'))
    },
    removeClickCap(id) {
      this.clickCaps = this.clickCaps.filter((c) => c.id !== id)
    },
    addConversionCap() {
      this.conversionCaps.push(newCap('conversion'))
    },
    removeConversionCap(id) {
      if (this.conversionCaps.length <= 1) return
      this.conversionCaps = this.conversionCaps.filter((c) => c.id !== id)
    },
    toggleExpand(id) {
      this.$set(this.expandedIds, id, !this.expandedIds[id])
    },
    addPayoutRow() {
      this.payoutRows.push(newPayoutRow({ _addN: '' }))
    },
    duplicatePayoutRow(id) {
      const src = this.payoutRows.find((r) => r.id === id)
      if (!src) return
      const copy = newPayoutRow({
        ...src,
        custom: { ...src.custom },
        cities: (src.cities || []).map((c) => ({ ...c })),
        devices: (src.devices || []).slice(),
        _addN: '',
        _countryOpen: false,
        _countryMatches: src.country
          ? [{ value: src.country, label: src._countryQuery || src.country }]
          : [],
        _countryBrowsingAll: false,
        _cityOpen: false,
        _cityQuery: '',
        _cityMatches: [],
        _citySearching: false,
      })
      const idx = this.payoutRows.findIndex((r) => r.id === id)
      this.payoutRows.splice(idx + 1, 0, copy)
    },
    deletePayoutRow(id) {
      this.payoutRows = this.payoutRows.filter((r) => r.id !== id)
      if (!this.payoutRows.length) this.payoutRows = [newPayoutRow({ _addN: '' })]
    },
    onPayoutsEdit(row, val) {
      row.payouts = String(val).replace(/[^0-9.]/g, '')
      row.payoutsEdited = true
    },
    advCustomKeys(row) {
      return Object.keys(row.custom || {})
        .filter((k) => k !== '2' && k !== '3' && k !== '_addN')
        .sort((a, b) => Number(a) - Number(b))
    },
    availableCustom(row) {
      return ADV_CUSTOM_NUMBERS.filter((n) => !(String(n) in (row.custom || {})))
    },
    addCustom(row) {
      if (!row._addN) return
      this.$set(row.custom, String(row._addN), '')
      row._addN = ''
    },
    removeCustom(row, n) {
      this.$delete(row.custom, n)
    },
    withManualOrigin(links) {
      if (!this.isOneLink) return links || []
      return applyOriginOverrideToLinks(links, this.manualUrl)
    },
    syncGeneratedLinkFields() {
      const first = (this.generatedLinks || [])[0]
      this.trackingUrl = (first && first.click) || ''
      this.impressionUrl = (first && first.impression) || ''
    },
    applyManualOriginToCurrentLinks() {
      if (!this.isOneLink || !(this.generatedLinks || []).length) return
      this.generatedLinks = this.withManualOrigin(this.generatedLinks)
      this.syncGeneratedLinkFields()
    },
    onManualUrlInput(value) {
      const next = stripWhitespaceIfPresent(value)
      if (next.stripped) this.notifyError('One Link URL should not contain white spaces')
      this.manualUrl = sanitizeManualOrigin(next.value)
      if (this.usesLinkGenerator) this.applyManualOriginToCurrentLinks()
      else this.syncManualTrackingLinks()
    },
    onAgencyOtherInput(value) {
      const next = stripWhitespaceIfPresent(value)
      if (next.stripped) this.notifyError('Agency name should not contain white spaces')
      this.afAgencyOther = next.value
    },
    onGeneratedUrlInput(link, field, value) {
      const next = stripWhitespaceIfPresent(value)
      if (next.stripped) this.notifyError('URL should not contain white spaces')
      this.$set(link, field, next.value)
      this.syncGeneratedLinkFields()
    },
    onPastedTrackingUrl(field, value) {
      const next = stripWhitespaceIfPresent(value)
      if (next.stripped) this.notifyError('URL should not contain white spaces')
      this[field] = next.value
      this.syncManualTrackingLinks()
    },
    generatedLinkEditKey(index, kind) {
      return index + ':' + kind
    },
    isGeneratedLinkEditing(index, kind) {
      return !!this.generatedLinkEdits[this.generatedLinkEditKey(index, kind)]
    },
    toggleGeneratedLinkEdit(index, kind) {
      const key = this.generatedLinkEditKey(index, kind)
      this.$set(this.generatedLinkEdits, key, !this.generatedLinkEdits[key])
    },
    resetGeneratedLinkEdits() {
      this.generatedLinkEdits = {}
    },
    openAfGenerator() {
      this.afCanApply = false
      this.tool = 'appsflyer'
    },
    openAdjustGenerator() {
      this.adjustCanApply = false
      this.tool = 'adjust'
    },
    syncManualTrackingLinks() {
      if (this.usesLinkGenerator) return
      let click = String(this.trackingUrl || '').trim()
      let impression = this.isVTA ? String(this.impressionUrl || '').trim() : ''
      if (this.isOneLink) {
        const rewritten = this.withManualOrigin([{ click, impression: impression || null }])[0]
        click = (rewritten && rewritten.click) || click
        impression = (rewritten && rewritten.impression) || impression
        this.trackingUrl = click
        if (this.isVTA) this.impressionUrl = impression
      }
      if (!click && !impression) {
        this.generatedLinks = []
        return
      }
      this.generatedLinks = [{
        account: this.mmpLabel || this.mmp || 'Manual',
        click: click || '',
        impression: impression || null,
      }]
    },
    confirmAfLinks() {
      if (this.$refs.afGenerator) this.$refs.afGenerator.applyLinks()
    },
    confirmAdjustLinks() {
      if (this.$refs.adjustGenerator) this.$refs.adjustGenerator.applyLinks()
    },
    onAfLinksApply(payload) {
      const links = (payload && payload.links) || []
      if (!links.length) return
      this.generatedLinks = this.withManualOrigin(links)
      this.resetGeneratedLinkEdits()
      this.syncGeneratedLinkFields()
      this.tool = null
    },
    onAdjustLinksApply(payload) {
      const link = (payload && payload.links && payload.links[0]) || null
      if (link) {
        if (link.click) this.trackingUrl = link.click
        if (link.impression) this.impressionUrl = link.impression
      } else {
        const resultLink = (payload && payload.resultLink) || ''
        if (!resultLink) return
        if (payload.linkType === 'impression') {
          this.impressionUrl = resultLink
        } else {
          this.trackingUrl = resultLink
        }
      }
      this.generatedLinks = this.withManualOrigin([{
        account: 'Adjust',
        click: this.trackingUrl || '',
        impression: this.impressionUrl || null,
      }])
      this.resetGeneratedLinkEdits()
      this.syncGeneratedLinkFields()
      this.tool = null
    },
    formReadyError() {
      if (this.isAppsFlyer && this.isOtherAgency && !this.resolvedAfAgency) {
        return 'Enter an AppsFlyer agency name.'
      }
      if (this.isOneLink && !sanitizeManualOrigin(this.manualUrl)) {
        return 'One Link URL is required for One link attribution.'
      }
      return ''
    },
    openReview() {
      this.affiseError = this.formReadyError()
      this.reviewOpen = true
    },
    goToOffersList() {
      this.reviewOpen = false
      this.$router.push({ name: 'NewGlobalOffers' })
    },
    buildMultiPatch() {
      const patch = {}
      if (this.country) patch.country = this.country
      if (this.offerType) patch.offer_type = this.offerType
      if (this.resolvedAfAgency) patch.agency = this.resolvedAfAgency
      const advUrl = String(this.advDefaultUrl || '').trim()
      if (advUrl) patch.advertiser_default_url = advUrl
      const tracking = String(this.trackingUrl || '').trim()
      if (tracking) patch.tracking_url = tracking
      const caps = clickCapsForPayload(this.clickCaps)
      if (!this.clickCaps.length || caps.length) patch.click_caps = caps
      const hasPayoutData = (this.payoutRows || []).some((row) => {
        if (String(row.goalValue || '').trim()) return true
        if (String(row.revenue || '').trim()) return true
        if (String(row.payouts || '').trim()) return true
        if (String(row.country || '').trim()) return true
        if (row.os && row.os !== 'All') return true
        if ((row.cities || []).length) return true
        if ((row.devices || []).length) return true
        return Object.keys(row.custom || {}).some((n) => String(row.custom[n] || '').trim())
      })
      if (hasPayoutData) {
        const temp = buildFileFlowPayload({
          selectedAdv: { affiseAdvId: '0' },
          advertiserId: '0',
          bundleId: '0',
          country: this.country || '',
          attribution: 'CTA',
          offerType: this.offerType || 'CPA',
          mmp: 'AF',
          afAgency: this.resolvedAfAgency,
          advDefaultUrl: this.advDefaultUrl,
          clickCaps: this.clickCaps,
          payoutRows: this.payoutRows,
          offerNames: [{ account: '', name: 'bulk', variant: 'CTA' }],
          generatedLinks: [],
          trackingUrl: 'https://placeholder.local',
          impressionUrl: '',
          sessionItems: {},
        })
        if (temp.common && temp.common.payouts && temp.common.payouts.length) {
          patch.payouts = temp.common.payouts
        }
      }
      return patch
    },
    async submitAffise() {
      if (this.isMultiEditMode) {
        await this.submitMultiEdit()
        return
      }
      this.affiseLastSubmitWasUpdate = this.hasFileFlowSession
      this.affiseError = this.formReadyError()
      this.affiseResults = []
      if (this.affiseError) return
      const payload = buildFileFlowPayload({
        selectedAdv: this.selectedAdv,
        advertiserId: this.advertiserId,
        bundleId: this.bundleId,
        bundle: this.bundle,
        country: this.country,
        attribution: this.attribution,
        offerType: this.offerType,
        mmp: this.mmp,
        afAgency: this.resolvedAfAgency,
        advDefaultUrl: this.advDefaultUrl,
        comments: this.comments,
        clickCaps: this.clickCaps,
        payoutRows: this.payoutRows,
        offerNames: this.offerNames,
        generatedLinks: this.generatedLinks,
        trackingUrl: this.trackingUrl,
        impressionUrl: this.impressionUrl,
        sessionItems: this.fileFlowSessionItems,
      })

      if (!payload.common.advertiser_affise_id) {
        this.affiseError = 'Select an advertiser with an Affise ID.'
        return
      }
      if (!payload.items.length) {
        this.affiseError = 'No offers to create — fill in the required fields.'
        return
      }
      const missing = payload.items.find((item) => !item.title || !item.tracking_url)
      if (missing) {
        if (!missing.title) {
          this.affiseError = 'Offer name is empty — check advertiser, bundle, and MMP.'
        } else if (missing.variant === 'VTA imp') {
          this.affiseError = this.usesLinkGenerator
            ? 'VTA impression URL is missing. Generate an impression link before sending.'
            : 'VTA impression URL is missing. Paste an impression tracking URL before sending.'
        } else {
          this.affiseError = this.usesLinkGenerator
            ? 'Tracking URL is missing. Generate a click link before sending.'
            : 'Tracking URL is missing. Paste a click tracking URL before sending.'
        }
        return
      }

      this.affiseSubmitting = true
      try {
        const res = await this.$http.post(this.resources.GlobalOffers.createGlobalOfferFileFlow, payload)
        const data = res.body || JSON.parse(res.bodyText || '{}')
        if (data.res !== 'OK') {
          this.affiseError = data.data || 'Request failed'
          this.affiseResults = []
          return
        }

        const apiData = data.data || {}
        const results = apiData.results || []
        this.fileFlowSessionItems = syncFileFlowSessionFromResults(this.fileFlowSessionItems, results)
        this.affiseResults = mapFileFlowResultsToAffiseDisplay(results)

        if (this.isEditMode && this.editSessionOffers.length) {
          const namesByKey = {}
          ;(this.offerNames || []).forEach((o) => {
            namesByKey[`${o.account || ''}::${o.variant || ''}`] = o.name
          })
          this.editSessionOffers = this.editSessionOffers.map((o) => {
            const key = `${o.account || ''}::${o.variant || ''}`
            if (!namesByKey[key]) return o
            return Object.assign({}, o, { title: namesByKey[key] })
          })
        }

        if (apiData.failed_count > 0 && !this.affiseHasSuccessfulResults) {
          const firstErr = results.find((r) => r.error)
          this.affiseError = firstErr ? firstErr.error : `All ${apiData.failed_count} offer(s) failed.`
        } else if (apiData.failed_count > 0) {
          this.affiseError = `${apiData.failed_count} offer(s) failed; ${apiData.success_count} succeeded.`
        }
        
      } catch (err) {
        console.error('createGlobalOfferFileFlow failed', err)
        this.affiseResults = []
        this.affiseError = (err && err.body && err.body.data) || (err && err.bodyText) || (err && err.message) || 'Failed to sync offers with Affise'
      } finally {
        this.affiseSubmitting = false
      }
    },
    async submitMultiEdit() {
      this.affiseLastSubmitWasUpdate = true
      this.affiseError = ''
      this.affiseResults = []
      const ids = (this.multiEditIds || []).map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
      if (!ids.length) {
        this.affiseError = 'No offers selected.'
        return
      }
      const patch = this.buildMultiPatch()
      if (!Object.keys(patch).length) {
        this.affiseError = 'Fill at least one field to apply to the selected offers.'
        return
      }

      this.affiseSubmitting = true
      try {
        const res = await this.$http.post(this.resources.GlobalOffers.bulkUpdateNewGlobalOffers, {
          ids,
          patch,
        })
        const data = res.body || JSON.parse(res.bodyText || '{}')
        if (data.res !== 'OK') {
          this.affiseError = data.data || 'Request failed'
          this.affiseResults = []
          return
        }
        const apiData = data.data || {}
        const results = apiData.results || []
        this.affiseResults = mapFileFlowResultsToAffiseDisplay(results)
        if (apiData.failed_count > 0 && !this.affiseHasSuccessfulResults) {
          const firstErr = results.find((r) => r.error)
          this.affiseError = firstErr ? firstErr.error : `All ${apiData.failed_count} offer(s) failed.`
        } else if (apiData.failed_count > 0) {
          this.affiseError = `${apiData.failed_count} offer(s) failed; ${apiData.success_count} succeeded.`
        }
      } catch (err) {
        console.error('bulkUpdateNewGlobalOffers failed', err)
        this.affiseResults = []
        this.affiseError =
          (err && err.body && err.body.data) ||
          (err && err.bodyText) ||
          (err && err.message) ||
          'Failed to update selected offers'
      } finally {
        this.affiseSubmitting = false
      }
    },
    async copyResults() {
      await copyToClipboard(buildOfferCopyText(this.affiseResults))
      this.copied = true
      if (this._copiedTimer) clearTimeout(this._copiedTimer)
      this._copiedTimer = setTimeout(() => {
        this.copied = false
      }, 1500)
    },
    async copyOfferNames() {
      if (!this.affiseHasSuccessfulResults) return
      const blocks = (this.affiseResults || [])
        .filter((r) => r && (r.name || r.globalOfferId))
        .map((r) => {
          const affiseId = (r.affise && r.affise[0] && r.affise[0].id) || ''
          return {
            globalOfferId: r.globalOfferId,
            affiseOfferId: affiseId,
            name: r.name || '',
          }
        })
      const comments = String(this.comments || '').trim()
      const text = buildOfferNamesCopyText(blocks, comments)
      if (!text) return
      await copyToClipboard(text)
      this.copiedNames = true
      if (this._copiedNamesTimer) clearTimeout(this._copiedNamesTimer)
      this._copiedNamesTimer = setTimeout(() => {
        this.copiedNames = false
      }, 1500)
    },
    clearAffiseResults() {
      this.affiseResults = []
      this.affiseError = ''
      this.reviewOpen = false
    },
    resetForm() {
      this.clearAdvertiser()
      this.clearBundle()
      this.clearCountry()
      this.advDefaultUrl = ''
      this.advImpDefaultUrl = ''
      this.comments = ''
      this.attribution = 'CTA'
      this.offerType = 'CPA'
      this.mmp = 'AF'
      this.afAccounts = []
      this.afAgency = 'none'
      this.afAgencyOther = ''
      this.trackingUrl = ''
      this.impressionUrl = ''
      this.manualUrl = ''
      this.kpiRows = []
      this.clickCaps = [newCap('click')]
      this.conversionCaps = [newCap('conversion')]
      this.budgetCap = newCap('budget')
      this.payoutRows = [newPayoutRow({ _addN: '' })]
      this.expandedIds = {}
      this.generatedLinks = []
      this.generatedLinkEdits = {}
      this.offerFreeText = ''
      this.affiseResults = []
      this.affiseError = ''
      this.reviewOpen = false
      this.fileFlowSessionItems = {}
      this.editOfferId = null
      this.duplicateFromId = null
      this.editSessionOffers = []
      this.multiEditIds = []
      this.advUnlockedForEdit = false
      this.editLoadError = ''
    },
  },
}
</script>
